import React, { useState, useEffect, useCallback, useMemo } from "react";
import { useParams, useNavigate, useLocation } from "react-router-dom";
import { Icon } from "@iconify/react";
import Button from "../../Components/Button";
import Card from "../../Components/Card";
import { Typography } from "../../Components/Typography";
import ProgressTimeline from "../../Components/ProgressTimeline";
import Avatar from "../../Components/Avatar";
import Tabs from "../../Components/Tabs";
import Table from "../../Components/Table";
import { useAuth } from "../../Context/authContext";
import {
  getIdeaById,
  getIdeaStages,
  getIdeasByUserId,
  getMentorIdeas,
  submitMentorStageAnswers,
  updateIdea,
  sendEmail,
  getUsersByRole,
  adminRejectOnBehalf,
  adminRejectIdea,
  reassignIdeaMentor,
  createIdeaMentorPreference,
  inviteSecondaryMentor,
  respondToMentorInvite,
  updateIdeaUser,
  getAllIdeaUsers,
  getTeamInvitations,
  getAllConfigurations,
} from "../../Utils/apiServices";
import {
  getMaxAccessibleStageIndex,
  canAccessStage,
  getFrontierStageIndex,
  getIdeaFlowStageName,
  getActiveStageIndexFromCurrentStep,
  openFileUrl,
  IDEA_FLOW_STAGE_NAMES,
  extractIdeasData,
  mapMentorContact,
  mapStudentContact,
  resolveStudentFromRecord,
} from "../IdeasReview/utils";
import ReviewScoresSection from "./ReviewScoresSection";
import IdeaOverviewSection from "./IdeaOverviewSection";
import MentorPreferencesPanel from "../../Components/MentorPreferencesPanel";
import IdeaClarityDialog from "../../Components/IdeaClarityDialog";
import Dialog from "../../Components/Dialog";
import AddTeamMemberDialog from "./AddTeamMemberDialog";
import IdeaAttachmentsSection from "./IdeaAttachmentsSection";
import EditableStageFieldsPanel from "./EditableStageFieldsPanel";
import EditablePitchPreparation from "./EditablePitchPreparation";
import {
  buildIdeaFormFromEngagement,
  buildPitchPayload,
  MARKET_STRATEGY_FIELDS,
  EXECUTION_PLAN_FIELDS,
  GROWTH_STRATEGY_FIELDS,
} from "./ideaFormConfig";
import {
  formatIdeaDate,
  formatSubmitterDisplayName,
  getFileIcon,
  formatFileSize,
  getEngagementStageTabs,
  getAttachmentSaveFieldKeys,
  readIdeaUrlField,
} from "./ideaDetailUiUtils";
import toast from "../../Utils/toastManager";

const MentorEngagementDetail = ({ isReadOnly = false, reviewReadOnly = false } = {}) => {
  const { id } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const { user } = useAuth();
  // Pending secondary-mentor invitation context — set when this page is opened
  // from the My Engagements "Mentor Invitations" banner. The invited mentor can
  // review the whole idea here, then Accept/Decline before becoming a co-mentor.
  const [pendingInvite, setPendingInvite] = useState(
    Boolean(location.state?.pendingInvite)
  );
  const inviteId = location.state?.inviteId ?? null;
  const inviterName = location.state?.inviterName ?? "";
  const [respondingInvite, setRespondingInvite] = useState(false);
  // Students: edit idea fields, read-only review. Mentors: read-only idea fields,
  // edit review. Admins and not-yet-accepted invitees: everything read-only.
  const reviewLocked = isReadOnly || reviewReadOnly || pendingInvite;
  const [engagement, setEngagement] = useState(null);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState("overview");
  const [activeStage, setActiveStage] = useState(0);
  // Initialize with default rubrics so they're always visible
  const [editedRubrics, setEditedRubrics] = useState([
    { id: 1, name: "Problem Clarity", score: 0, maxScore: 10, icon: "lucide:monitor", color: "bg-blue-500" },
    { id: 2, name: "Vision and Ambition", score: 0, maxScore: 10, icon: "lucide:shopping-bag", color: "bg-pink-500" },
    { id: 3, name: "Originality", score: 0, maxScore: 10, icon: "lucide:shopping-cart", color: "bg-blue-600" },
    { id: 4, name: "Impact Potential", score: 0, maxScore: 10, icon: "lucide:link", color: "bg-green-500" },
    { id: 5, name: "Research Depth", score: 0, maxScore: 10, icon: "lucide:link", color: "bg-green-500" },
  ]);
  // Per-stage "Review Outcome & Feedback" from the mentor: { [stageId]: string }.
  // Mandatory for every stage before the idea can advance to the next stage.
  const [stageReviewOutcomes, setStageReviewOutcomes] = useState({});
  const [isSaving, setIsSaving] = useState(false);
  const [isAdvancing, setIsAdvancing] = useState(false);
  // True when the mentor has edited scores/notes without saving — blocks "Approve to Next Stage".
  const [reviewDirty, setReviewDirty] = useState(false);
  // Evaluation stages from GET /idea/:ideaId/stages (dynamic per idea)
  const [evaluationStages, setEvaluationStages] = useState(null);
  const [evaluationStagesLoading, setEvaluationStagesLoading] = useState(false);
  const [evaluationStagesError, setEvaluationStagesError] = useState(null);
  // Per-stage, per-question answers and scores: { [stageId]: { [questionId]: { answer, score } } }
  const [reviewResponses, setReviewResponses] = useState({});
  // Stage IDs (as strings) this mentor has already saved scores for. Once a
  // stage is here, the mentor's scores/notes become read-only — initialized
  // from the API on load and augmented after each successful save.
  // Answer modal: { stageId, questionId, question, draft } | null
  const [answerModal, setAnswerModal] = useState(null);
  const [ideaForm, setIdeaForm] = useState({});
  const [originalIdeaForm, setOriginalIdeaForm] = useState({});
  const [isEditMode, setIsEditMode] = useState(false);
  const [savingIdeaForm, setSavingIdeaForm] = useState(false);
  const [showClarityDialog, setShowClarityDialog] = useState(false);
  const [savingClarity, setSavingClarity] = useState(false);
  const [showRejectDialog, setShowRejectDialog] = useState(false);
  // Which reject action the shared dialog performs on confirm:
  //   null       -> primary mentor's terminal reject (handleRejectIdea)
  //   'onBehalf' -> admin declines for the currently-assigned mentor (handoff)
  //   'terminal' -> admin terminally rejects the idea
  const [adminRejectMode, setAdminRejectMode] = useState(null);
  const [rejectingIdea, setRejectingIdea] = useState(false);
  const [rejectReason, setRejectReason] = useState("");
  const [rejectReasonError, setRejectReasonError] = useState("");
  const [showReassignDialog, setShowReassignDialog] = useState(false);
  const [mentors, setMentors] = useState([]);
  const [loadingMentors, setLoadingMentors] = useState(false);
  const [reassigning, setReassigning] = useState(false);
  const [reassignError, setReassignError] = useState("");
  // idea_users rows for this idea (students + mentors) — drives the Team
  // Members table and the secondary-student view-only restriction.
  const [ideaTeamRows, setIdeaTeamRows] = useState([]);
  // Pending co-founder invitations (emailed, not yet accepted).
  const [pendingTeamInvites, setPendingTeamInvites] = useState([]);
  const [showAddTeamMember, setShowAddTeamMember] = useState(false);
  // Per-idea limits from the admin configuration (fallbacks until loaded).
  const [maxSecondaryMentors, setMaxSecondaryMentors] = useState(2);
  const [maxStudents, setMaxStudents] = useState(4);
  // Invite-secondary-mentor dialog (primary mentor only)
  const [showInviteMentor, setShowInviteMentor] = useState(false);
  const [inviteMentors, setInviteMentors] = useState([]);
  const [loadingInviteMentors, setLoadingInviteMentors] = useState(false);
  // Search + industry filter for the Invite a Mentor dialog.
  const [inviteSearch, setInviteSearch] = useState("");
  const [inviteIndustryFilter, setInviteIndustryFilter] = useState("");
  // Secondary mentor whose stage scores the primary mentor is viewing (modal).
  const [viewSecondaryMentorId, setViewSecondaryMentorId] = useState(null);
  const [invitingMentor, setInvitingMentor] = useState(false);
  const [inviteError, setInviteError] = useState("");
  const [inviteSuccess, setInviteSuccess] = useState("");

  // The student who created the idea (PRIMARY_STUDENT) can edit it and add
  // team members; SECONDARY_STUDENT collaborators get view-only access.
  const isPrimaryStudent =
    user?.role === "student" &&
    ideaTeamRows.some(
      (row) =>
        row?.role === "PRIMARY_STUDENT" &&
        String(row?.user_id ?? row?.user?.id) === String(user?.id)
    );
  const isSecondaryStudent =
    user?.role === "student" &&
    !isPrimaryStudent &&
    ideaTeamRows.some(
      (row) =>
        row?.role === "SECONDARY_STUDENT" &&
        String(row?.user_id ?? row?.user?.id) === String(user?.id)
    );

  // The primary mentor is the one stored on the student idea_users rows
  // (idea.decided_by, copied to mentor_id on accept). Only they can invite a
  // secondary co-mentor, and only until the idea reaches its secondary-mentor cap.
  const isPrimaryMentor =
    user?.role === "mentor" &&
    ideaTeamRows.some(
      (row) =>
        (row?.role === "PRIMARY_STUDENT" || row?.role === "SECONDARY_STUDENT") &&
        String(row?.mentor_id) === String(user?.id)
    );
  // An idea can have one primary mentor and up to `maxSecondaryMentors` secondary
  // mentors, plus at most `maxStudents` students (primary + secondary) in total —
  // both limits come from the admin configuration.
  const secondaryMentorCount = ideaTeamRows.filter(
    (row) => row?.role === "SECONDARY_MENTOR"
  ).length;
  const canInviteSecondaryMentor = secondaryMentorCount < maxSecondaryMentors;
  const studentCount = ideaTeamRows.filter(
    (row) =>
      row?.role === "PRIMARY_STUDENT" || row?.role === "SECONDARY_STUDENT"
  ).length;
  const canAddStudent =
    studentCount + pendingTeamInvites.length < maxStudents;

  // Distinct industries among the invitable mentors (for the filter dropdown).
  const inviteIndustryOptions = useMemo(() => {
    const set = new Set();
    inviteMentors.forEach((m) => {
      const industry = (m.industry || "").trim();
      if (industry) set.add(industry);
    });
    return Array.from(set).sort((a, b) => a.localeCompare(b));
  }, [inviteMentors]);

  // Mentors matching the current search term and industry filter.
  const filteredInviteMentors = useMemo(() => {
    const query = inviteSearch.trim().toLowerCase();
    return inviteMentors.filter((m) => {
      if (inviteIndustryFilter && (m.industry || "").trim() !== inviteIndustryFilter) {
        return false;
      }
      if (!query) return true;
      const haystack = [
        m.first_name,
        m.last_name,
        `${m.first_name || ""} ${m.last_name || ""}`,
        m.email,
        m.profession,
        m.organization,
        m.industry,
        m.functional_experience,
        m.city,
        m.country,
      ]
        .filter(Boolean)
        .join(" ")
        .toLowerCase();
      return haystack.includes(query);
    });
  }, [inviteMentors, inviteSearch, inviteIndustryFilter]);

  // Secondary-mentor reviews for the active stage — only the primary mentor can
  // see these. Built from the per-question reviews returned for the current viewer
  // (the backend only sends a secondary mentor's scores to the primary mentor).
  const activeStageData = evaluationStages?.stages?.[activeStage] || null;
  const secondaryReviewers = useMemo(() => {
    if (!isPrimaryMentor || !activeStageData) return [];
    const primaryIdStr = user?.id != null ? String(user.id) : null;
    const byMentor = new Map();
    (activeStageData.questions || []).forEach((q) => {
      (Array.isArray(q.reviews) ? q.reviews : []).forEach((r) => {
        const mid = r.mentorId != null ? String(r.mentorId) : null;
        if (!mid || mid === primaryIdStr) return;
        if (!byMentor.has(mid)) {
          const name =
            `${r.mentor?.first_name || ""} ${r.mentor?.last_name || ""}`.trim() ||
            r.mentor?.email ||
            "Mentor";
          byMentor.set(mid, { mentorId: mid, name, scored: 0 });
        }
        if (r.score != null) byMentor.get(mid).scored += 1;
      });
    });
    return Array.from(byMentor.values());
  }, [isPrimaryMentor, activeStageData, user?.id]);

  // The selected secondary mentor's per-question scores/notes for the active stage.
  const selectedSecondaryReview = useMemo(() => {
    if (!viewSecondaryMentorId || !activeStageData) return null;
    const midStr = String(viewSecondaryMentorId);
    const questions = (activeStageData.questions || []).map((q) => {
      const r = (Array.isArray(q.reviews) ? q.reviews : []).find(
        (rr) => String(rr.mentorId) === midStr
      );
      return { question: q.question, score: r?.score ?? null, answer: r?.answer ?? "" };
    });
    const reviewer = secondaryReviewers.find((s) => s.mentorId === midStr);
    const total = questions.reduce((sum, x) => sum + (Number(x.score) || 0), 0);
    // This secondary mentor's own "Review Outcome & Feedback" for the stage.
    const outcome =
      (Array.isArray(activeStageData.mentorOutcomes) ? activeStageData.mentorOutcomes : []).find(
        (o) => String(o.mentorId) === midStr
      )?.outcome ?? "";
    return {
      name: reviewer?.name || "Mentor",
      questions,
      total,
      maxTotal: questions.length * 10,
      outcome,
    };
  }, [viewSecondaryMentorId, activeStageData, secondaryReviewers]);
  const canEditIdeaFields = !isReadOnly && reviewReadOnly && !isSecondaryStudent;

  // Students see read-only fields by default; click "Edit Details" to enable editing.
  const canEditNow = canEditIdeaFields && isEditMode;

  const fetchIdeaTeam = useCallback(async () => {
    if (!id) return;
    try {
      const [teamRes, invitesRes] = await Promise.all([
        getAllIdeaUsers({ idea_id: id }),
        getTeamInvitations(id, "PENDING"),
      ]);
      const rows = teamRes?.data?.data ?? teamRes?.data ?? [];
      setIdeaTeamRows(Array.isArray(rows) ? rows : []);
      const invites = invitesRes?.data?.data ?? invitesRes?.data ?? [];
      setPendingTeamInvites(Array.isArray(invites) ? invites : []);
    } catch (err) {
      console.error("Error loading idea team members:", err);
    }
  }, [id]);

  useEffect(() => {
    fetchIdeaTeam();
  }, [fetchIdeaTeam]);

  // Load per-idea limits (secondary mentors, students) from admin configuration.
  useEffect(() => {
    let active = true;
    (async () => {
      try {
        const res = await getAllConfigurations();
        const payload = res?.data?.data ?? res?.data;
        const config = Array.isArray(payload) ? payload[0] : payload;
        if (!active || !config) return;
        const mentors = Number(config.secondary_mentor_per_idea);
        const students = Number(config.student_per_idea);
        if (Number.isFinite(mentors) && mentors > 0) setMaxSecondaryMentors(mentors);
        if (Number.isFinite(students) && students > 0) setMaxStudents(students);
      } catch (err) {
        console.error("Error loading per-idea limits:", err);
      }
    })();
    return () => {
      active = false;
    };
  }, []);

  // Transform API response to component format
  const transformEngagementData = (apiData) => {
    if (!apiData) return null;

    // Handle different possible response structures
    const data = apiData.data?.data || apiData.data || apiData;
    const ideaData = data.idea || data;
    const mentorRaw =
      data.mentor ??
      data.assignedMentor ??
      data.assigned_mentor ??
      ideaData.mentor ??
      null;

    return {
      id: ideaData.id,
      title: ideaData.title || ideaData.name || ideaData.idea_name || "",
      description: ideaData.description || "",
      status: ideaData.status || ideaData.idea_status || "",
      submissionDate: ideaData.submission_date || ideaData.submissionDate || ideaData.created_at || "",
      /** Raw API value only (no description fallback) — used to lock edits for students after first save */
      hasPersistedProblemStatement: Boolean(
        String(ideaData.problem_statement ?? ideaData.problemStatement ?? "").trim()
      ),
      problemStatement: ideaData.problem_statement || ideaData.problemStatement || ideaData.description || "",
      proposedSolution: ideaData.proposed_solution || ideaData.proposedSolution || ideaData.description || "",
      targetAudience: ideaData.target_audience || ideaData.targetAudience || "",
      existingAlternatives: ideaData.existing_alternatives || ideaData.existingAlternatives || "",
      primaryImpact: ideaData.primary_impact || ideaData.primaryImpact || "",
      ideaVideoLink: readIdeaUrlField(ideaData, "idea_video_link", "ideaVideoLink"),
      conceptDiagram: readIdeaUrlField(ideaData, "concept_diagram", "conceptDiagram"),
      pitchVideoLink: readIdeaUrlField(ideaData, "pitch_video_link", "pitchVideoLink"),
      otherDocument: readIdeaUrlField(ideaData, "other_document", "otherDocument"),
      ideaImage: readIdeaUrlField(ideaData, "idea_image", "ideaImage"),
      ideaClarity: ideaData.idea_clarity || ideaData.ideaClarity || "",
      submitter: resolveStudentFromRecord(data) || {
        name: "",
        avatar: "",
        email: "",
      },
      mentor: mapMentorContact(mentorRaw),
      // Used by the admin "Reassign mentor" flow.
      studentId: resolveStudentFromRecord(data)?.id ?? ideaData.student_id ?? null,
      ideaUserId:
        data.idea_user_id ??
        data.ideaUserId ??
        (Array.isArray(data.idea_users || data.ideaUsers)
          ? (data.idea_users || data.ideaUsers).find((row) =>
              /primary_student|student/i.test(String(row?.role || ""))
            )?.id
          : null) ??
        null,
      rubricsScores: ideaData.rubrics_scores || ideaData.rubricsScores || [],
      customerDeepDiveNotes: ideaData.customer_deep_dive_notes || ideaData.customerDeepDiveNotes || "",
      numberOfMentors: ideaData.number_of_mentors || ideaData.numberOfMentors || 0,
      overallScore: ideaData.overall_score || ideaData.overallScore || 0,
      maxOverallScore: ideaData.max_overall_score || ideaData.maxOverallScore || 50,
      decisionReason: ideaData.decision_reason || ideaData.decisionReason || "",
      rejectionMessage:
        ideaData.rejection_message ||
        ideaData.rejectionMessage ||
        ideaData.decision_reason ||
        ideaData.decisionReason ||
        "",
      teamMembers: ideaData.team_members || ideaData.teamMembers || [],
      // Extract files including links from API response
      files: (() => {
        const linkFiles = [];
        const submissionDate = ideaData.submission_date || ideaData.submissionDate || ideaData.created_at || "";
        const ideaVideoUrl = readIdeaUrlField(ideaData, "idea_video_link", "ideaVideoLink");
        const conceptDiagramUrl = readIdeaUrlField(ideaData, "concept_diagram", "conceptDiagram");
        const pitchVideoUrl = readIdeaUrlField(ideaData, "pitch_video_link", "pitchVideoLink");
        const otherDocumentUrl = readIdeaUrlField(ideaData, "other_document", "otherDocument");
        const ideaImageUrl = readIdeaUrlField(ideaData, "idea_image", "ideaImage");

        if (ideaVideoUrl) {
          linkFiles.push({
            id: `idea-video-${ideaData.id}`,
            name: "Idea Video Link",
            size: "Link",
            type: "mp4",
            fileTag: "Video",
            uploadedOn: submissionDate ? new Date(submissionDate).toLocaleDateString("en-GB", {
              day: "numeric",
              month: "short",
              year: "numeric",
            }) : "-",
            url: ideaVideoUrl,
            isLink: true,
          });
        }

        if (conceptDiagramUrl) {
          linkFiles.push({
            id: `concept-diagram-${ideaData.id}`,
            name: "Concept Diagram Link",
            size: "Link",
            type: "link",
            fileTag: "Diagram",
            uploadedOn: submissionDate ? new Date(submissionDate).toLocaleDateString("en-GB", {
              day: "numeric",
              month: "short",
              year: "numeric",
            }) : "-",
            url: conceptDiagramUrl,
            isLink: true,
          });
        }

        if (pitchVideoUrl) {
          linkFiles.push({
            id: `pitch-video-${ideaData.id}`,
            name: "Pitch Video Link",
            size: "Link",
            type: "mp4",
            fileTag: "Video",
            uploadedOn: submissionDate ? new Date(submissionDate).toLocaleDateString("en-GB", {
              day: "numeric",
              month: "short",
              year: "numeric",
            }) : "-",
            url: pitchVideoUrl,
            isLink: true,
          });
        }

        if (otherDocumentUrl) {
          const urlLower = otherDocumentUrl.toLowerCase();
          let fileType = "doc";
          if (urlLower.includes(".pdf")) fileType = "pdf";
          else if (urlLower.includes(".docx")) fileType = "docx";
          else if (urlLower.includes(".doc")) fileType = "doc";
          else if (urlLower.includes(".jpg") || urlLower.includes(".jpeg") || urlLower.includes(".png")) {
            fileType = "jpg";
          }

          linkFiles.push({
            id: `other-document-${ideaData.id}`,
            name: "Other Document Link",
            size: "Link",
            type: fileType,
            fileTag: "Document",
            uploadedOn: submissionDate ? new Date(submissionDate).toLocaleDateString("en-GB", {
              day: "numeric",
              month: "short",
              year: "numeric",
            }) : "-",
            url: otherDocumentUrl,
            isLink: true,
          });
        }

        if (ideaImageUrl) {
          linkFiles.push({
            id: `idea-image-${ideaData.id}`,
            name: "Idea Image",
            size: "Link",
            type: "jpg",
            fileTag: "Image",
            uploadedOn: submissionDate ? new Date(submissionDate).toLocaleDateString("en-GB", {
              day: "numeric",
              month: "short",
              year: "numeric",
            }) : "-",
            url: ideaImageUrl,
            isLink: true,
          });
        }

        const existingFiles = ideaData.files || ideaData.attachments || [];
        return [...linkFiles, ...existingFiles];
      })(),
      currentStage: ideaData.current_stage || ideaData.currentStage || 0,
      currentStep: ideaData.current_step ?? 1,
      daysRemaining: ideaData.days_remaining || data.daysRemaining || null,
      stages: ideaData.stages || [],
      // Engagement-specific data (placeholder - will be populated from API)
      meetingNotes: ideaData.meeting_notes || ideaData.meetingNotes || [],
      actionItems: ideaData.action_items || ideaData.actionItems || [],
      recommendations: ideaData.recommendations || [],
      // Market Strategy data — mapped from flat API keys (e.g. ideal_customer_profile, not ideal_customer_profile_description)
      marketStrategy: {
        idealCustomerProfile: {
          description: ideaData.ideal_customer_profile || "",
          file: ideaData.ideal_customer_profile_url ? { url: ideaData.ideal_customer_profile_url, name: "View Resource", type: "link", size: "Link" } : null,
          date: "",
        },
        valueProposition: {
          description: ideaData.value_proposition || "",
          file: ideaData.value_proposition_url ? { url: ideaData.value_proposition_url, name: "View Resource", type: "link", size: "Link" } : null,
          date: "",
        },
        productMarketFit: {
          description: ideaData.product_market_fit || "",
          file: ideaData.product_market_fit_url ? { url: ideaData.product_market_fit_url, name: "View Resource", type: "link", size: "Link" } : null,
          date: "",
        },
        targetAddressableMarket: {
          description: ideaData.target_addressable_market || "",
          file: ideaData.target_addressable_market_url ? { url: ideaData.target_addressable_market_url, name: "View Resource", type: "link", size: "Link" } : null,
          date: "",
        },
      },
      // Execution Plan data
      executionPlan: {
        mvp: {
          description: ideaData.mvp || "",
          file: ideaData.mvp_url ? { url: ideaData.mvp_url, name: "View Resource", type: "link", size: "Link" } : null,
          date: "",
        },
        marketValidation: {
          description: ideaData.market_validation || "",
          file: ideaData.market_validation_url ? { url: ideaData.market_validation_url, name: "View Resource", type: "link", size: "Link" } : null,
          date: "",
        },
        financialProjections: {
          description: ideaData.financial_projections || "",
          file: ideaData.financial_projections_url ? { url: ideaData.financial_projections_url, name: "View Resource", type: "link", size: "Link" } : null,
          date: "",
        },
        microGrantProposal: {
          description: ideaData.micro_grant_proposal || "",
          file: ideaData.micro_grant_proposal_url ? { url: ideaData.micro_grant_proposal_url, name: "View Resource", type: "link", size: "Link" } : null,
          date: "",
        },
      },
      // Growth Strategy data
      growthStrategy: {
        enhanceBusinessModel: {
          description: ideaData.enhance_business_model || "",
          file: ideaData.enhance_business_model_url ? { url: ideaData.enhance_business_model_url, name: "View Resource", type: "link", size: "Link" } : null,
          date: "",
          category: "",
        },
        productReadiness: {
          description: ideaData.product_readiness || "",
          file: ideaData.product_readiness_url ? { url: ideaData.product_readiness_url, name: "View Resource", type: "link", size: "Link" } : null,
          date: "",
          category: "",
        },
        customerProfile: {
          description: ideaData.customer_profile || "",
          file: ideaData.customer_profile_url ? { url: ideaData.customer_profile_url, name: "View Resource", type: "link", size: "Link" } : null,
          date: "",
          category: "",
        },
        gtmStrategy: {
          description: ideaData.gtm_strategy || "",
          file: ideaData.gtm_strategy_url ? { url: ideaData.gtm_strategy_url, name: "View Resource", type: "link", size: "Link" } : null,
          date: "",
          category: "",
        },
        productMarketFit: {
          description: "",
          file: null,
          date: "",
          category: "",
        },
        targetAddressableMarket: {
          description: "",
          file: null,
          date: "",
          category: "",
        },
        customerFeedback: {
          description: ideaData.customer_feedback || "",
          file: ideaData.customer_feedback_url ? { url: ideaData.customer_feedback_url, name: "View Resource", type: "link", size: "Link" } : null,
          date: "",
          category: "",
        },
      },
      // Pitch Preparation data
      pitchPreparation: {
        investorPitch: {
          problemStatement: ideaData.investor_pitch_problem_statement || ideaData.investorPitch?.problemStatement || false,
          productMarketFit: ideaData.investor_pitch_product_market_fit || ideaData.investorPitch?.productMarketFit || false,
          customerProfile: ideaData.investor_pitch_customer_profile || ideaData.investorPitch?.customerProfile || false,
          competitiveLandscape: ideaData.investor_pitch_competitive_landscape || ideaData.investorPitch?.competitiveLandscape || false,
          targetAddressableMarket: ideaData.investor_pitch_target_addressable_market || ideaData.investorPitch?.targetAddressableMarket || false,
        },
        marketTraction: {
          revenue: ideaData.market_traction_revenue || ideaData.marketTraction?.revenue || false,
          margin: ideaData.market_traction_margin || ideaData.marketTraction?.margin || false,
        },
        metrics: {
          sales: ideaData.metrics_sales || ideaData.metrics?.sales || false,
          pipeline: ideaData.metrics_pipeline || ideaData.metrics?.pipeline || false,
          marketing: ideaData.metrics_marketing || ideaData.metrics?.marketing || false,
          followers: ideaData.metrics_followers || ideaData.metrics?.followers || false,
          customerAcquisitionCost: ideaData.metrics_customer_acquisition_cost || ideaData.metrics?.customerAcquisitionCost || false,
        },
        additional: {
          productVideoLink: ideaData.product_video_link || ideaData.additional?.productVideoLink || "",
          pitchDeckUpload: ideaData.pitch_deck_upload || ideaData.additional?.pitchDeckUpload || null,
        },
      },
    };
  };

  const hasSubmitterInfo = (submitter) =>
    Boolean(
      submitter?.name?.trim() ||
        submitter?.email?.trim()
    );

  const enrichEngagementParticipants = useCallback(
    async (transformedData) => {
      if (!transformedData) return null;

      let result = { ...transformedData };

      if (!result.mentor) {
        const mentorFromNav = location.state?.mentor;
        if (mentorFromNav) {
          result.mentor = mapMentorContact(mentorFromNav) || mentorFromNav;
        }
      }

      if (!hasSubmitterInfo(result.submitter)) {
        const studentFromNav =
          location.state?.primaryStudent || location.state?.student;
        const fromNav = mapStudentContact(studentFromNav);
        if (fromNav) result.submitter = fromNav;
      }

      const needsMentor = !result.mentor;
      const needsStudent = !hasSubmitterInfo(result.submitter);

      if ((needsMentor || needsStudent) && user?.id) {
        try {
          if (user.role === "student") {
            const ideasRes = await getIdeasByUserId(user.id);
            const records = extractIdeasData(ideasRes);
            const match = records.find(
              (row) => String(row?.idea?.id ?? row?.idea_id) === String(id)
            );
            if (match) {
              if (needsMentor) {
                const mentorFromList = mapMentorContact(match?.mentor);
                if (mentorFromList) result.mentor = mentorFromList;
              }
              if (needsStudent) {
                const studentFromList =
                  resolveStudentFromRecord(match) || mapStudentContact(match?.user);
                if (studentFromList) result.submitter = studentFromList;
              }
            }
          } else if (user.role === "mentor") {
            const ideasRes = await getMentorIdeas(user.id);
            const records = extractIdeasData(ideasRes);
            const match = records.find(
              (row) => String(row?.idea?.id ?? row?.idea_id) === String(id)
            );
            if (match) {
              if (needsStudent) {
                const studentFromList =
                  resolveStudentFromRecord(match) ||
                  mapStudentContact(match?.primaryStudent);
                if (studentFromList) result.submitter = studentFromList;
              }
            }
          }
        } catch (lookupError) {
          console.error("Error loading idea participants:", lookupError);
        }
      }

      if (!hasSubmitterInfo(result.submitter) && user?.role === "student") {
        const self = mapStudentContact(user);
        if (self) result.submitter = self;
      }

      if (!result.mentor && user?.role === "mentor") {
        const selfMentor = mapMentorContact(user);
        if (selfMentor) result.mentor = selfMentor;
      }

      return result;
    },
    [
      id,
      location.state?.mentor,
      location.state?.primaryStudent,
      location.state?.student,
      user,
    ]
  );

  useEffect(() => {
    const fetchEngagementData = async () => {
      try {
        setLoading(true);

        // Try to fetch from API
        const response = await getIdeaById(id);

        if (response?.success && response.data) {
          const baseData = transformEngagementData(response);
          if (baseData) {
            const transformedData = await enrichEngagementParticipants(baseData);
            setEngagement(transformedData);
            const maxStage = getMaxAccessibleStageIndex(transformedData.currentStep ?? 1);
            const fromNav = location.state?.initialStage;
            const stageFromStep = getActiveStageIndexFromCurrentStep(
              transformedData.currentStep ?? 1
            );
            const resolvedStage =
              typeof fromNav === "number"
                ? Math.min(maxStage, Math.max(0, fromNav))
                : stageFromStep;
            setActiveStage(resolvedStage);
            // Initialize editable fields - use API data if available, otherwise keep defaults
            if (transformedData.rubricsScores && transformedData.rubricsScores.length > 0) {
              setEditedRubrics(transformedData.rubricsScores);
            }
            // Otherwise, keep the default rubrics that were initialized in useState
            const initialForm = buildIdeaFormFromEngagement(transformedData);
            setIdeaForm(initialForm);
            setOriginalIdeaForm(initialForm);
            setLoading(false);
            return;
          }
        }
      } catch (error) {
        console.error("Error fetching engagement:", error);
      } finally {
        setLoading(false);
      }
    };

    if (id) {
      fetchEngagementData();
    }
  }, [id, enrichEngagementParticipants, location.state?.initialStage]);

  // Fetch stages + questions for this idea (for Review & Score)
  useEffect(() => {
    const fetchStages = async () => {
      if (!id) return;
      setEvaluationStagesLoading(true);
      setEvaluationStagesError(null);
      try {
        const response = await getIdeaStages(id, user?.id);
        const payload = response?.data?.data ?? response?.data;
        if (response?.success && payload?.stages) {
          const { ideaId, stages } = payload;
          setEvaluationStages({ ideaId, stages: stages || [] });
          // Initialize reviewResponses with existing reviews from API (mentor's own review preferred)
          const mentorIdStr = user?.id != null ? String(user.id) : null;
          const initial = {};
          const initialOutcomes = {};
          (stages || []).forEach((stage) => {
            const sid = String(stage.stageId);
            initial[sid] = {};
            initialOutcomes[sid] = stage.reviewOutcome || "";
            (stage.questions || []).forEach((q) => {
              const reviews = Array.isArray(q.reviews) ? q.reviews : [];
              // Only ever load the viewer's OWN review into the EDITABLE fields —
              // never another mentor's, so one mentor's scores can't prefill another's.
              const ownReview = mentorIdStr
                ? reviews.find((r) => String(r.mentorId) === mentorIdStr)
                : null;
              // Read-only viewers (student/admin) have no review of their own, so
              // show the mentor review the API decided they may see — otherwise the
              // Review & Scores tab would render every rubric as 0.
              const sourceReview =
                ownReview ?? (reviewLocked ? reviews[0] ?? null : null);
              initial[sid][String(q.id)] = {
                answer: sourceReview?.answer ?? "",
                score: sourceReview?.score ?? null,
              };
            });
          });
          setReviewResponses((prev) => ({ ...prev, ...initial }));
          setStageReviewOutcomes((prev) => ({ ...prev, ...initialOutcomes }));
          // Freshly loaded data reflects what's saved on the server — no unsaved changes yet.
          setReviewDirty(false);
        }
      } catch (err) {
        setEvaluationStagesError(err?.error || "Failed to load stages");
        setEvaluationStages(null);
      } finally {
        setEvaluationStagesLoading(false);
      }
    };
    fetchStages();
  }, [id, user?.id, reviewLocked]);

  // Reset active tab to overview when stage changes
  useEffect(() => {
    setActiveTab("overview");
  }, [activeStage]);

  // Keep viewed stage within mentor-approved progress (current_step)
  useEffect(() => {
    if (!engagement) return;
    const maxStage = getMaxAccessibleStageIndex(engagement.currentStep);
    if (activeStage > maxStage) {
      setActiveStage(maxStage);
    }
  }, [engagement?.currentStep, engagement, activeStage]);

  const maxAccessibleStageIndex = engagement
    ? getMaxAccessibleStageIndex(engagement.currentStep)
    : 0;

  // Scores/notes for a stage lock only once the idea has been approved to a LATER
  // stage — i.e. the viewed stage is behind the current (frontier) stage. Saving
  // scores does NOT lock them; the mentor can keep editing the current stage until
  // they approve the idea forward.
  const scoresLocked = engagement
    ? activeStage < getFrontierStageIndex(engagement.currentStep)
    : false;

  const handleStageClick = (stageIndex) => {
    if (!engagement) return;
    if (canAccessStage(stageIndex, engagement.currentStep)) {
      setActiveStage(stageIndex);
    }
  };

  const prepareStageReviewSubmit = useCallback(
    ({ requireAllScored = false } = {}) => {
      const currentStageFromApi = evaluationStages?.stages?.[activeStage];
      const mentorId = user?.id;
      const frontierStageIndex = getFrontierStageIndex(engagement?.currentStep ?? 1);

      if (activeStage !== frontierStageIndex) {
        return {
          error:
            "You can only submit a review for the current stage awaiting mentor approval.",
        };
      }

      if (!evaluationStages?.stages?.length || !currentStageFromApi || !mentorId) {
        return { error: "Review questions are not available for this stage." };
      }

      const stageId = Number(currentStageFromApi.stageId);
      const questions = currentStageFromApi.questions || [];
      const responsesForStage =
        reviewResponses[String(currentStageFromApi.stageId)] || {};

      if (!questions.length) {
        return { error: "No review questions are configured for this stage." };
      }

      const answers = questions.map((q) => {
        const answerText = responsesForStage[String(q.id)]?.answer ?? "";
        const rawScore = responsesForStage[String(q.id)]?.score;
        const score =
          rawScore != null && rawScore !== "" ? Number(rawScore) : undefined;
        return {
          stageId,
          questionId: Number(q.id),
          answer: answerText,
          ...(score != null && !Number.isNaN(score) ? { score } : {}),
        };
      });

      const reviewOutcomeMessage =
        stageReviewOutcomes[String(currentStageFromApi.stageId)] ?? "";

      if (requireAllScored) {
        // Approving to the next stage requires a COMPLETE review: every question
        // scored AND noted, plus the stage's Review Outcome & Feedback.
        const allScored = questions.every((q) => {
          const rawScore = responsesForStage[String(q.id)]?.score;
          return (
            rawScore != null && rawScore !== "" && !Number.isNaN(Number(rawScore))
          );
        });
        if (!allScored) {
          return {
            error:
              "Please provide a score for every review question before advancing to the next stage.",
          };
        }

        const missingNotes = questions.filter(
          (q) => !String(responsesForStage[String(q.id)]?.answer || "").trim()
        );
        if (missingNotes.length) {
          return {
            error: `Add a note for every question before advancing to the next stage. Missing notes for: ${missingNotes
              .map((q) => q.question)
              .join(", ")}.`,
          };
        }

        // The Review Outcome & Feedback is mandatory for every stage before
        // the idea can advance to the next stage.
        if (!reviewOutcomeMessage.trim()) {
          return {
            error:
              "Please add the Review Outcome & Feedback for this stage before advancing to the next stage.",
          };
        }
      }

      // Saving allows a PARTIAL review — any subset of scores and notes can be
      // saved and finished later. Nothing is required to save.

      return {
        mentorId,
        answers,
        reviewOutcome: { stageId, message: reviewOutcomeMessage },
      };
    },
    [activeStage, engagement?.currentStep, evaluationStages, reviewResponses, stageReviewOutcomes, user?.id]
  );

  // Wrapped setters: any mentor edit to scores/notes marks the review as having unsaved changes.
  const handleReviewResponsesChange = useCallback((updater) => {
    setReviewDirty(true);
    setReviewResponses(updater);
  }, []);
  const handleEditedRubricsChange = useCallback((updater) => {
    setReviewDirty(true);
    setEditedRubrics(updater);
  }, []);
  const handleStageReviewOutcomeChange = useCallback((stageId, value) => {
    setReviewDirty(true);
    setStageReviewOutcomes((prev) => ({ ...prev, [String(stageId)]: value }));
  }, []);

  // Once the idea is rejected, the whole review becomes read-only for the mentor.
  const isIdeaRejected = String(engagement?.status || "").toUpperCase() === "REJECTED";

  const handleSaveScores = useCallback(async () => {
    if (isIdeaRejected) {
      toast.warning("This idea has been rejected — scores can no longer be updated.");
      return;
    }
    const payload = prepareStageReviewSubmit({ requireAllScored: false });
    if (payload.error) {
      toast.error(payload.error);
      return;
    }

    setIsSaving(true);
    try {
      const res = await submitMentorStageAnswers(id, {
        mentor_id: payload.mentorId,
        answers: payload.answers,
        review_outcome: payload.reviewOutcome,
      });

      if (!res?.success) {
        console.error("Submit review failed:", res);
        toast.error("Failed to save scores. Please try again.");
        return;
      }

      setEngagement((prev) =>
        prev
          ? {
              ...prev,
              rubricsScores: editedRubrics,
            }
          : prev
      );
      // Scores are now persisted — clear the unsaved-changes guard so Approve is allowed.
      // Scores stay editable after saving; they only lock once the idea is
      // approved to the next stage (handled by the stage-position check).
      setReviewDirty(false);
    } catch (error) {
      console.error("Error saving review:", error);
      toast.error("An error occurred while saving scores.");
    } finally {
      setIsSaving(false);
    }
  }, [editedRubrics, id, prepareStageReviewSubmit, isIdeaRejected]);

  const handleAdvanceStage = useCallback(async () => {
    if (isIdeaRejected) {
      toast.warning("This idea has been rejected — it can no longer advance to the next stage.");
      return;
    }
    const payload = prepareStageReviewSubmit({ requireAllScored: true });
    if (payload.error) {
      toast.error(payload.error);
      return;
    }

    const currentStep = engagement?.currentStep ?? 1;
    const maxStep =
      evaluationStages?.stages?.length || IDEA_FLOW_STAGE_NAMES.length;
    if (currentStep >= maxStep) {
      toast.warning("This idea has already reached the final stage.");
      return;
    }

    setIsAdvancing(true);
    try {
      const res = await submitMentorStageAnswers(id, {
        mentor_id: payload.mentorId,
        answers: payload.answers,
        review_outcome: payload.reviewOutcome,
      });

      if (!res?.success) {
        console.error("Submit review failed:", res);
        toast.error("Failed to save review before advancing. Please try again.");
        return;
      }

      const newStep = currentStep + 1;
      const updateRes = await updateIdea(id, { current_step: newStep });

      if (!updateRes?.success) {
        console.error("Failed to advance idea step:", updateRes);
        toast.error(updateRes?.error || "Review saved, but the idea could not advance to the next stage.");
        return;
      }

      setEngagement((prev) =>
        prev
          ? {
              ...prev,
              currentStep: newStep,
              rubricsScores: editedRubrics,
            }
          : prev
      );
      setActiveStage(getActiveStageIndexFromCurrentStep(newStep));
      setReviewDirty(false);
    } catch (error) {
      console.error("Error advancing stage:", error);
      toast.error("An error occurred while advancing the stage.");
    } finally {
      setIsAdvancing(false);
    }
  }, [
    editedRubrics,
    engagement?.currentStep,
    evaluationStages?.stages?.length,
    id,
    prepareStageReviewSubmit,
    isIdeaRejected,
  ]);

  const formatDate = formatIdeaDate;

  const reloadEngagement = async () => {
    const response = await getIdeaById(id);
    if (response?.success && response.data) {
      const baseData = transformEngagementData(response);
      if (baseData) {
        const transformedData = await enrichEngagementParticipants(baseData);
        setEngagement((prev) => ({
          ...transformedData,
          mentor: transformedData.mentor || prev?.mentor || null,
          submitter: hasSubmitterInfo(transformedData.submitter)
            ? transformedData.submitter
            : prev?.submitter || transformedData.submitter,
        }));
        const refreshedForm = buildIdeaFormFromEngagement(transformedData);
        setIdeaForm(refreshedForm);
        setOriginalIdeaForm(refreshedForm);
      }
    }
  };

  const handleIdeaFormChange = (field, value) => {
    setIdeaForm((prev) => ({ ...prev, [field]: value }));
  };

  const ALL_EDITABLE_FIELD_KEYS = [
    "problem_statement",
    "proposed_solution",
    "target_audience",
    "existing_alternatives",
    "primary_impact",
    "idea_video_link",
    "concept_diagram",
    "other_document",
    "idea_image",
    "pitch_video_link",
    ...MARKET_STRATEGY_FIELDS.flatMap((f) => [f.field, f.urlField].filter(Boolean)),
    ...EXECUTION_PLAN_FIELDS.flatMap((f) => [f.field, f.urlField].filter(Boolean)),
    ...GROWTH_STRATEGY_FIELDS.flatMap((f) => [f.field, f.urlField].filter(Boolean)),
  ];

  const handleStartEditDetails = () => {
    setOriginalIdeaForm(ideaForm);
    setIsEditMode(true);
  };

  const handleCancelEditDetails = () => {
    setIdeaForm(originalIdeaForm);
    setIsEditMode(false);
  };

  const handleSaveEditDetails = async () => {
    await handleSaveIdeaFields(ALL_EDITABLE_FIELD_KEYS, { pitchExtras: true });
    setIsEditMode(false);
  };

  const handleSaveIdeaFields = async (fieldKeys, { pitchExtras = false } = {}) => {
    let keysToSave = fieldKeys;
    // Students cannot change problem statement once it exists on the idea (backend truth).
    if (user?.role === "student" && engagement?.hasPersistedProblemStatement) {
      keysToSave = keysToSave.filter((k) => k !== "problem_statement");
    }

    const payload = {};
    keysToSave.forEach((key) => {
      const val = ideaForm[key];
      payload[key] = typeof val === "string" ? val.trim() : val;
    });
    if (pitchExtras) {
      Object.assign(payload, buildPitchPayload(ideaForm));
    }

    if (!Object.keys(payload).length) {
      return;
    }

    setSavingIdeaForm(true);
    try {
      const response = await updateIdea(id, payload);
      if (response?.success) {
        await reloadEngagement();
      } else {
        toast.error(response?.error || "Failed to save changes.");
      }
    } catch (error) {
      console.error("Error saving idea:", error);
      toast.error("An error occurred while saving your changes.");
    } finally {
      setSavingIdeaForm(false);
    }
  };

  const sparkOverviewFieldKeys = [
    "problem_statement",
    "proposed_solution",
    "target_audience",
    "existing_alternatives",
    "primary_impact",
  ];

  const keysFromFieldConfig = (config) =>
    config.flatMap((item) => [item.field, item.urlField].filter(Boolean));

  // While reviewing a pending invitation the mentor isn't on the idea yet, so
  // they can only Accept/Decline — not request clarity or reject the idea.
  const canRequestClarity = user?.role === "mentor" && !isReadOnly && !pendingInvite;
  // Only the primary mentor (not a secondary co-mentor, students, or admins) can
  // reject an idea — the same mentor who controls stage progression.
  const canRejectIdea =
    user?.role === "mentor" && !isReadOnly && !pendingInvite && isPrimaryMentor;
  // Only admins (read-only viewers) can reassign the mentor on an active idea.
  const canReassign = user?.role === "admin";

  const handleRejectIdea = async () => {
    const reason = rejectReason.trim();
    if (!reason) {
      setRejectReasonError("Please provide a reason for rejecting this idea.");
      return;
    }

    setRejectingIdea(true);
    try {
      const response = await updateIdea(id, { status: "REJECTED", decision_reason: reason });
      if (response?.success) {
        setEngagement((prev) =>
          prev ? { ...prev, status: "REJECTED", rejectionMessage: reason } : prev
        );
        setShowRejectDialog(false);
        setRejectReason("");
        setRejectReasonError("");
      } else {
        toast.error(response?.error || "Failed to reject the idea. Please try again.");
      }
    } catch (error) {
      console.error("Error rejecting idea:", error);
      toast.error(error?.error || "An error occurred while rejecting the idea.");
    } finally {
      setRejectingIdea(false);
    }
  };

  // Admin can reject on behalf of the currently-assigned mentor only while the
  // idea is still awaiting a mentor decision (PENDING). Terminal reject is allowed
  // any time the idea isn't already rejected.
  const ideaStatusUpper = String(engagement?.status || "").toUpperCase();
  const canAdminRejectOnBehalf = user?.role === "admin" && ideaStatusUpper === "PENDING";
  const canAdminTerminalReject = user?.role === "admin" && ideaStatusUpper !== "REJECTED";

  const openAdminReject = (mode) => {
    setAdminRejectMode(mode);
    setRejectReason("");
    setRejectReasonError("");
    setShowRejectDialog(true);
  };

  const handleAdminRejectOnBehalf = async () => {
    const reason = rejectReason.trim();
    if (!reason) {
      setRejectReasonError("Please provide a reason for rejecting on the mentor's behalf.");
      return;
    }
    setRejectingIdea(true);
    try {
      const response = await adminRejectOnBehalf(id, reason);
      if (response?.success) {
        const awaitingAdmin = response?.data?.data?.awaitingAdmin;
        setShowRejectDialog(false);
        setAdminRejectMode(null);
        setRejectReason("");
        setRejectReasonError("");
        toast.success(
          awaitingAdmin
            ? "Rejected on the mentor's behalf. No preferred mentors remain — the idea stays pending for your review. Use \"Reject Idea\" to close it or reassign a mentor."
            : "Rejected on the mentor's behalf. The idea has moved to the student's next preferred mentor."
        );
      } else {
        toast.error(response?.error || "Failed to reject on the mentor's behalf. Please try again.");
      }
    } catch (error) {
      console.error("Error rejecting on mentor's behalf:", error);
      toast.error(error?.error || "An error occurred while rejecting on the mentor's behalf.");
    } finally {
      setRejectingIdea(false);
    }
  };

  const handleAdminTerminalReject = async () => {
    const reason = rejectReason.trim();
    if (!reason) {
      setRejectReasonError("Please provide a reason for rejecting this idea.");
      return;
    }
    setRejectingIdea(true);
    try {
      const response = await adminRejectIdea(id, reason);
      if (response?.success) {
        setEngagement((prev) =>
          prev ? { ...prev, status: "REJECTED", rejectionMessage: reason } : prev
        );
        setShowRejectDialog(false);
        setAdminRejectMode(null);
        setRejectReason("");
        setRejectReasonError("");
        toast.success("Idea rejected. The student has been notified.");
      } else {
        toast.error(response?.error || "Failed to reject the idea. Please try again.");
      }
    } catch (error) {
      console.error("Error rejecting idea:", error);
      toast.error(error?.error || "An error occurred while rejecting the idea.");
    } finally {
      setRejectingIdea(false);
    }
  };

  // The shared reject dialog dispatches to the right handler based on the mode set
  // when its trigger button was clicked.
  const handleConfirmReject = () => {
    if (adminRejectMode === "onBehalf") return handleAdminRejectOnBehalf();
    if (adminRejectMode === "terminal") return handleAdminTerminalReject();
    return handleRejectIdea();
  };

  const handleOpenReassign = async () => {
    if (isIdeaRejected) return;

    setShowReassignDialog(true);
    setReassignError("");
    setLoadingMentors(true);
    try {
      const response = await getUsersByRole("mentor");
      let list = [];
      if (response?.success) {
        const payload = response.data?.data ?? response.data;
        list = Array.isArray(payload) ? payload : [];
      }
      const approved = list.filter((m) => m.mentor_status === "approved");
      setMentors(approved.length ? approved : list);
    } catch (error) {
      console.error("Error fetching mentors:", error);
      setReassignError("Failed to load mentors. Please try again.");
    } finally {
      setLoadingMentors(false);
    }
  };

  const handleReassignMentor = async (mentor) => {
    const studentId = engagement?.studentId ?? engagement?.submitter?.id;
    if (!engagement?.id || !studentId || !mentor?.id) {
      setReassignError("Missing required data to reassign this mentor.");
      return;
    }
    setReassigning(true);
    setReassignError("");
    try {
      // Reassign directly to this mentor (same or different). The backend makes them
      // the sole current assignee — reusing/reviving their preference row as needed —
      // and emails them, so this works even if they were a prior preference.
      const res = await reassignIdeaMentor(engagement.id, mentor.id, studentId);
      if (!(res?.success || res?.status === 200 || res?.status === 201)) {
        setReassignError(res?.error || "Failed to reassign the mentor.");
        return;
      }

      // Remove the previously-mapped mentor from the idea-user mapping.
      if (engagement.ideaUserId != null) {
        await updateIdeaUser(engagement.ideaUserId, {
          idea_id: engagement.id,
          mentor_id: null,
        });
      }

      await reloadEngagement();
      setShowReassignDialog(false);
    } catch (error) {
      console.error("Error reassigning mentor:", error);
      setReassignError(error?.error || "An error occurred while reassigning the mentor.");
    } finally {
      setReassigning(false);
    }
  };

  const handleOpenInviteMentor = async () => {
    setShowInviteMentor(true);
    setInviteError("");
    setInviteSuccess("");
    setInviteSearch("");
    setInviteIndustryFilter("");
    setLoadingInviteMentors(true);
    try {
      const response = await getUsersByRole("mentor");
      let list = [];
      if (response?.success) {
        const payload = response.data?.data ?? response.data;
        list = Array.isArray(payload) ? payload : [];
      }
      const approved = list.filter((m) => m.mentor_status === "approved");
      // Exclude myself and anyone already mentoring this idea.
      const excluded = new Set([
        String(user?.id),
        ...ideaTeamRows
          .filter((r) => r?.role === "SECONDARY_MENTOR")
          .map((r) => String(r.user_id ?? r.user?.id)),
      ]);
      const base = approved.length ? approved : list;
      setInviteMentors(base.filter((m) => !excluded.has(String(m.id))));
    } catch (error) {
      console.error("Error fetching mentors:", error);
      setInviteError("Failed to load mentors. Please try again.");
    } finally {
      setLoadingInviteMentors(false);
    }
  };

  // Mentor card title/role (mirrors the idea submission mentor selection UI)
  const getInviteMentorTitle = (mentor) => {
    if (mentor.profession) return mentor.profession;
    if (mentor.organization) return mentor.organization;
    if (mentor.functional_experience) return mentor.functional_experience;
    return "Mentor";
  };

  // Mentor card description (mirrors the idea submission mentor selection UI)
  const getInviteMentorDescription = (mentor) => {
    const parts = [];
    if (mentor.city && mentor.country) {
      parts.push(`${mentor.city}-based mentor`);
    } else if (mentor.country) {
      parts.push(`${mentor.country}-based mentor`);
    }
    if (mentor.industry) {
      parts.push(`specializing in ${mentor.industry}`);
    } else if (mentor.functional_experience) {
      parts.push(`specializing in ${mentor.functional_experience}`);
    }
    if (mentor.bio) {
      parts.push(mentor.bio);
    }
    return parts.length > 0
      ? parts.join(". ") + "."
      : "Experienced mentor ready to guide you.";
  };

  const handleInviteMentor = async (mentor) => {
    if (!id || !user?.id || !mentor?.id) {
      setInviteError("Missing required data to send the invitation.");
      return;
    }
    setInvitingMentor(true);
    setInviteError("");
    try {
      const res = await inviteSecondaryMentor({
        idea_id: id,
        mentor_id: mentor.id,
        invited_by: user.id,
      });
      if (res?.success || res?.status === 200 || res?.status === 201) {
        const mentorName =
          `${mentor.first_name || ""} ${mentor.last_name || ""}`.trim() ||
          mentor.email ||
          "the mentor";
        setInviteSuccess(`Invitation sent to ${mentorName}.`);
        await fetchIdeaTeam();
        setShowInviteMentor(false);
      } else {
        setInviteError(
          res?.error?.message || res?.error || "Failed to send the invitation."
        );
      }
    } catch (error) {
      console.error("Error inviting mentor:", error);
      setInviteError(error?.error || "An error occurred while sending the invitation.");
    } finally {
      setInvitingMentor(false);
    }
  };

  const handleRespondInvite = async (accept) => {
    if (!inviteId || !user?.id) return;
    setRespondingInvite(true);
    try {
      const res = await respondToMentorInvite(inviteId, {
        accept,
        mentor_id: user.id,
      });
      if (res?.success || res?.status === 200) {
        if (accept) {
          // Now a secondary co-mentor: unlock the page and reload as a real engagement.
          setPendingInvite(false);
          await Promise.all([reloadEngagement(), fetchIdeaTeam()]);
        } else {
          navigate("/engagements");
        }
      } else {
        toast.error(res?.error || "Failed to respond to the invitation.");
      }
    } catch (error) {
      console.error("Error responding to invitation:", error);
      toast.error(error?.error || "An error occurred while responding to the invitation.");
    } finally {
      setRespondingInvite(false);
    }
  };

  const handleSaveIdeaClarity = async (emailPayload) => {
    setSavingClarity(true);
    try {
      const emailResponse = await sendEmail(emailPayload);
      if (!emailResponse?.success) {
        toast.error(emailResponse?.error || "Failed to send clarity email.");
        return;
      }

      const response = await updateIdea(id, { idea_clarity: emailPayload.body.trim() });
      if (response?.success) {
        setEngagement((prev) => (prev ? { ...prev, ideaClarity: emailPayload.body.trim() } : prev));
        setShowClarityDialog(false);
      } else {
        toast.error(response?.error || "Email sent, but failed to save clarity request on the idea.");
      }
    } catch (error) {
      console.error("Error saving idea clarity:", error);
      toast.error(error?.error || "An error occurred while sending the clarity request.");
    } finally {
      setSavingClarity(false);
    }
  };

  if (loading) {
    return (
      <div className="flex flex-col w-full gap-6 bg-slate-50 px-8 py-8 min-h-screen">
        <div className="animate-pulse flex flex-col gap-6">
          <div className="h-8 w-72 rounded-lg bg-slate-200" />
          <div className="h-28 w-full rounded-2xl bg-slate-200" />
          <div className="h-12 w-full rounded-xl bg-slate-100" />
          <div className="h-96 w-full rounded-2xl bg-slate-100" />
        </div>
      </div>
    );
  }

  if (!engagement) {
    return (
      <div className="flex flex-col items-center justify-center h-screen gap-4">
        <Typography as="h2" className="text-2xl font-bold">
          Engagement not found
        </Typography>
        <Button onClick={() => navigate("/engagements")}>Go Back</Button>
      </div>
    );
  }

  return (
    <div className="flex flex-col w-full h-auto gap-6 bg-primary min-h-screen px-6 py-6 lg:px-10 lg:py-8">
      {/* Header */}
      <div className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
        <div className="flex items-center gap-4">
          <Button
            variant="secondary"
            size="medium"
            onClick={() => navigate(-1)}
            className="shrink-0 !rounded-full !p-2.5 aspect-square"
            aria-label="Back"
            title="Back"
          >
            <Icon icon="lucide:arrow-left" className="h-5 w-5" />
          </Button>
          <div>
            <Typography as="h1" className="text-2xl font-bold text-primary-900 tracking-tight">
              {engagement.title}
            </Typography>
            {engagement.submitter?.name && (
              <Typography as="p" className="text-sm text-tertiary-600 mt-1">
                by {engagement.submitter.name}
              </Typography>
            )}
          </div>
        </div>
        {(canRequestClarity || canRejectIdea) && (
          <div className="flex items-center gap-3 shrink-0">
            {canRequestClarity && (
              <Button
                variant="secondary"
                size="medium"
                disabled={isIdeaRejected}
                startIcon={<Icon icon="lucide:message-circle-question" className="h-5 w-5" />}
                onClick={() => setShowClarityDialog(true)}
                className="shrink-0 border-amber-300 text-amber-800 bg-amber-50 hover:bg-amber-100 disabled:opacity-60 disabled:cursor-not-allowed"
              >
                Request Clarity
              </Button>
            )}
            {canRejectIdea && (
              <Button
                variant="secondary"
                size="medium"
                disabled={isIdeaRejected}
                startIcon={<Icon icon="lucide:x-circle" className="h-5 w-5" />}
                onClick={() => setShowRejectDialog(true)}
                className="shrink-0 border-red-300 text-red-700 bg-red-50 hover:bg-red-100 disabled:opacity-60 disabled:cursor-not-allowed"
              >
                {isIdeaRejected ? "Idea Rejected" : "Reject Idea"}
              </Button>
            )}
          </div>
        )}
        {canReassign && (
          <Button
            variant="primary"
            size="medium"
            disabled={isIdeaRejected}
            startIcon={<Icon icon="lucide:user-cog" className="h-5 w-5" />}
            onClick={handleOpenReassign}
            className="shrink-0 bg-violet-600 hover:bg-violet-700 text-white disabled:hover:bg-violet-600"
          >
            Reassign
          </Button>
        )}
        {canAdminRejectOnBehalf && (
          <Button
            variant="secondary"
            size="medium"
            startIcon={<Icon icon="lucide:user-x" className="h-5 w-5" />}
            onClick={() => openAdminReject("onBehalf")}
            className="shrink-0 border-amber-300 text-amber-800 bg-amber-50 hover:bg-amber-100"
          >
            Reject on Mentor's Behalf
          </Button>
        )}
        {canAdminTerminalReject && (
          <Button
            variant="secondary"
            size="medium"
            startIcon={<Icon icon="lucide:x-circle" className="h-5 w-5" />}
            onClick={() => openAdminReject("terminal")}
            className="shrink-0 border-red-300 text-red-700 bg-red-50 hover:bg-red-100"
          >
            Reject Idea
          </Button>
        )}
        {canEditIdeaFields && !isEditMode && (
          <Button
            variant="primary"
            size="medium"
            startIcon={<Icon icon="lucide:pencil" className="h-5 w-5" />}
            onClick={handleStartEditDetails}
            className="shrink-0 bg-violet-600 hover:bg-violet-700 text-white"
          >
            Edit Details
          </Button>
        )}
        {canEditIdeaFields && isEditMode && (
          <div className="flex items-center gap-2 shrink-0">
            <Button
              variant="secondary"
              size="medium"
              onClick={handleCancelEditDetails}
              disabled={savingIdeaForm}
              className="border-slate-200 text-slate-700 hover:bg-slate-50"
            >
              Cancel
            </Button>
            <Button
              variant="primary"
              size="medium"
              onClick={handleSaveEditDetails}
              disabled={savingIdeaForm}
              className="bg-violet-600 hover:bg-violet-700 text-white"
              startIcon={
                savingIdeaForm ? (
                  <Icon icon="lucide:loader-2" className="h-5 w-5 animate-spin" />
                ) : (
                  <Icon icon="lucide:save" className="h-5 w-5" />
                )
              }
            >
              {savingIdeaForm ? "Saving..." : "Save"}
            </Button>
          </div>
        )}
      </div>

      {/* Admin: mentor preferences (1/2/3) while pending; active mentorship once accepted */}
      {user?.role === "admin" && (
        <MentorPreferencesPanel
          ideaId={id}
          ideaStatus={ideaStatusUpper}
          rejectionReason={
            engagement?.decisionReason || engagement?.rejectionMessage || ""
          }
        />
      )}

      {/* Secondary-mentor invitation: review the idea below, then decide */}
      {pendingInvite && (
        <div className="flex flex-col gap-3 rounded-2xl border border-violet-200 bg-violet-50 px-5 py-4 sm:flex-row sm:items-center sm:justify-between">
          <div className="flex items-start gap-3">
            <Icon icon="lucide:mail" className="h-5 w-5 text-violet-600 mt-0.5 shrink-0" />
            <div className="flex flex-col">
              <Typography as="p" className="text-sm font-semibold text-primary-900">
                You&apos;ve been invited to co-mentor this idea
                {inviterName ? ` by ${inviterName}` : ""}.
              </Typography>
              <Typography as="p" className="text-xs text-tertiary-600">
                Review the details below, then accept to become a secondary mentor or decline.
              </Typography>
            </div>
          </div>
          <div className="flex items-center gap-2 shrink-0">
            <Button
              variant="secondary"
              size="medium"
              disabled={respondingInvite}
              className="bg-white border border-slate-200 text-slate-700 hover:bg-slate-50"
              onClick={() => handleRespondInvite(false)}
            >
              Decline
            </Button>
            <Button
              variant="primary"
              size="medium"
              disabled={respondingInvite}
              startIcon={
                respondingInvite ? (
                  <Icon icon="lucide:loader-2" className="h-5 w-5 animate-spin" />
                ) : (
                  <Icon icon="lucide:check" className="h-5 w-5" />
                )
              }
              className="bg-violet-600 hover:bg-violet-700 text-white"
              onClick={() => handleRespondInvite(true)}
            >
              {respondingInvite ? "Accepting..." : "Accept Invitation"}
            </Button>
          </div>
        </div>
      )}

      {/* Progress Timeline - use API stages when available */}
      <ProgressTimeline
        stages={
          evaluationStages?.stages?.length
            ? evaluationStages.stages.map((s, index) => ({
                id: s.stageId,
                name:
                  getIdeaFlowStageName(index) ||
                  getIdeaFlowStageName(s.stageNumber, { zeroBased: false }) ||
                  s.name ||
                  `Stage ${s.stageNumber}`,
              }))
            : engagement?.stages || []
        }
        currentStage={activeStage}
        maxAccessibleStage={maxAccessibleStageIndex}
        daysRemaining={engagement?.daysRemaining || 27}
        onStageClick={handleStageClick}
      />

      {!isIdeaRejected &&
        reviewLocked &&
        maxAccessibleStageIndex <
          (evaluationStages?.stages?.length
            ? evaluationStages.stages.length - 1
            : 4) && (
        <div className="rounded-xl border border-violet-200/80 bg-gradient-to-r from-violet-50 to-indigo-50 px-4 py-3.5 flex items-start gap-3 shadow-sm">
          <div className="flex h-9 w-9 shrink-0 items-center justify-center rounded-lg bg-violet-100 border border-violet-200">
            <Icon icon="lucide:info" className="h-5 w-5 text-violet-600" />
          </div>
          <Typography as="p" className="text-sm text-violet-900 leading-relaxed pt-1">
            The next stage opens after your mentor saves their review and unlocks it for you.
          </Typography>
        </div>
      )}

      {/* Stage Content - Shows tabs content when Spark, Shape, Secure, Scale, or Shine stage is active */}
      {(activeStage === 0 || activeStage === 1 || activeStage === 2 || activeStage === 3 || activeStage === 4) && (
        <div className="w-full">
          <Tabs
            variant="segment"
            tabs={getEngagementStageTabs(activeStage)}
            activeTab={activeTab}
            onTabChange={setActiveTab}
          />

          <div className="py-6 min-h-[400px] text-left">
            {activeTab === "overview" && (
              <IdeaOverviewSection
                engagement={engagement}
                activeStage={activeStage}
                canEdit={canEditNow}
                readOnlyOverviewFieldKeys={
                  user?.role === "student" && engagement?.hasPersistedProblemStatement
                    ? ["problem_statement"]
                    : []
                }
                formValues={ideaForm}
                onFieldChange={handleIdeaFormChange}
                onSave={() => handleSaveIdeaFields(sparkOverviewFieldKeys)}
                saving={savingIdeaForm}
                hideSaveButton
              />
            )}

            {activeTab === "execution-plan" && canEditNow && (
              <EditableStageFieldsPanel
                title="Execution plan"
                subtitle="MVP, validation, finances, and grant materials."
                fields={EXECUTION_PLAN_FIELDS}
                formValues={ideaForm}
                onFieldChange={handleIdeaFormChange}
                onSave={() => handleSaveIdeaFields(keysFromFieldConfig(EXECUTION_PLAN_FIELDS))}
                saving={savingIdeaForm}
                accentClass="bg-emerald-600 hover:bg-emerald-700"
                hideSaveButton
              />
            )}

            {activeTab === "execution-plan" && !canEditNow && (
              <div className="flex flex-col gap-4 text-start">
                {/* Top Section - Meta Information */}
                <div className="flex items-center justify-between w-full">
                  <Typography
                    as="p"
                    className="text-sm font-medium text-purple-600"
                  >
                    Submitted: {formatDate(engagement.submissionDate)}
                  </Typography>
                  <div className="flex items-center gap-3">
                    <Avatar
                      src={
                        engagement.submitter?.avatar &&
                          engagement.submitter.avatar.length >= 10 &&
                          (engagement.submitter.avatar.startsWith("http") ||
                            engagement.submitter.avatar.startsWith("data:image") ||
                            engagement.submitter.avatar.startsWith("/"))
                          ? engagement.submitter.avatar
                          : null
                      }
                      firstName={engagement.submitter?.name?.split(" ")[0]}
                      lastName={engagement.submitter?.name?.split(" ").slice(1).join(" ")}
                      email={engagement.submitter?.email}
                      alt={engagement.submitter?.name}
                      size="w-8 h-8"
                    />
                    <Typography
                      as="p"
                      className="text-sm font-medium text-primary-900"
                    >
                      {formatSubmitterDisplayName(engagement.submitter) || "Student"}
                    </Typography>
                  </div>
                </div>

                {/* Execution Plan Sections */}
                <div className="flex flex-col gap-4 text-start">
                  {/* MVP */}
                  <div className="flex flex-col gap-1.5">
                    <Typography
                      as="h3"
                      className="text-base font-semibold text-primary-900"
                    >
                      MVP
                    </Typography>
                    {engagement.executionPlan?.mvp?.description ? (
                    <Typography as="p" className="text-sm text-tertiary-600 leading-relaxed">
                      {engagement.executionPlan?.mvp.description}
                      </Typography>
                    ) : (
                    <Typography as="p" className="text-sm text-slate-400 italic">Not provided yet</Typography>
                    )}
                    {(engagement.executionPlan?.mvp?.file || engagement.executionPlan?.mvp?.date) && (
                      <div className="flex items-center justify-between mt-4 p-4 bg-slate-50 rounded-lg border border-slate-200">
                        {engagement.executionPlan?.mvp?.file && (
                          <div className="flex items-center gap-4 flex-1">
                            <div className={`w-12 h-12 ${getFileIcon(engagement.executionPlan.mvp.file.type || "docx").bg} rounded flex items-center justify-center flex-shrink-0`}>
                              <Typography
                                as="span"
                                className="text-xs font-semibold text-white"
                              >
                                {getFileIcon(engagement.executionPlan.mvp.file.type || "docx").text}
                              </Typography>
                            </div>
                            <div className="flex flex-col">
                              <Typography
                                as="p"
                                className="text-sm text-primary-900 font-medium"
                              >
                                {engagement.executionPlan.mvp.file.name || "Dashboard prototype recording.mp4"}
                              </Typography>
                              <Typography
                                as="p"
                                className="text-xs text-tertiary-500"
                              >
                                {formatFileSize(engagement.executionPlan.mvp.file.size || "16 MB")}
                              </Typography>
                            </div>
                          </div>
                        )}
                        <div className="flex items-center gap-4">
                          <Typography
                            as="p"
                            className="text-sm font-medium text-primary-900"
                          >
                            MVP
                          </Typography>
                          <Typography
                            as="p"
                            className="text-sm text-tertiary-600"
                          >
                            {engagement.executionPlan?.mvp?.date ? formatDate(engagement.executionPlan.mvp.date) : null}
                          </Typography>
                          {engagement.executionPlan?.mvp?.file?.url && (
                            <Button
                              variant="secondary"
                              size="small"
                              onClick={() => {
                                window.open(engagement.executionPlan.mvp.file.url, '_blank');
                              }}
                              className="text-purple-600 hover:text-purple-700 border-purple-200 hover:border-purple-300"
                            >
                              View
                            </Button>
                          )}
                        </div>
                      </div>
                    )}
                  </div>

                  {/* Market Validation */}
                  <div className="flex flex-col gap-1.5">
                    <Typography
                      as="h3"
                      className="text-base font-semibold text-primary-900"
                    >
                      Market Validation
                    </Typography>
                    {engagement.executionPlan?.marketValidation?.description ? (
                    <Typography as="p" className="text-sm text-tertiary-600 leading-relaxed">
                      {engagement.executionPlan?.marketValidation.description}
                      </Typography>
                    ) : (
                    <Typography as="p" className="text-sm text-slate-400 italic">Not provided yet</Typography>
                    )}
                    {(engagement.executionPlan?.marketValidation?.file || engagement.executionPlan?.marketValidation?.date) && (
                      <div className="flex items-center justify-between mt-4 p-4 bg-slate-50 rounded-lg border border-slate-200">
                        {engagement.executionPlan?.marketValidation?.file && (
                          <div className="flex items-center gap-4 flex-1">
                            <div className={`w-12 h-12 ${getFileIcon(engagement.executionPlan.marketValidation.file.type || "mp4").bg} rounded flex items-center justify-center flex-shrink-0`}>
                              <Typography
                                as="span"
                                className="text-xs font-semibold text-white"
                              >
                                {getFileIcon(engagement.executionPlan.marketValidation.file.type || "mp4").text}
                              </Typography>
                            </div>
                            <div className="flex flex-col">
                              <Typography
                                as="p"
                                className="text-sm text-primary-900 font-medium"
                              >
                                {engagement.executionPlan.marketValidation.file.name || "Dashboard prototype recording.mp4"}
                              </Typography>
                              <Typography
                                as="p"
                                className="text-xs text-tertiary-500"
                              >
                                {formatFileSize(engagement.executionPlan.marketValidation.file.size || "16 MB")}
                              </Typography>
                            </div>
                          </div>
                        )}
                        <div className="flex items-center gap-4">
                          <Typography
                            as="p"
                            className="text-sm font-medium text-primary-900"
                          >
                            Market Validation
                          </Typography>
                          <Typography
                            as="p"
                            className="text-sm text-tertiary-600"
                          >
                            {engagement.executionPlan?.marketValidation?.date ? formatDate(engagement.executionPlan.marketValidation.date) : null}
                          </Typography>
                          {engagement.executionPlan?.marketValidation?.file?.url && (
                            <Button
                              variant="secondary"
                              size="small"
                              onClick={() => {
                                window.open(engagement.executionPlan.marketValidation.file.url, '_blank');
                              }}
                              className="text-purple-600 hover:text-purple-700 border-purple-200 hover:border-purple-300"
                            >
                              View
                            </Button>
                          )}
                        </div>
                      </div>
                    )}
                  </div>

                  {/* Financial Projections */}
                  <div className="flex flex-col gap-1.5">
                    <Typography
                      as="h3"
                      className="text-base font-semibold text-primary-900"
                    >
                      Financial Projections
                    </Typography>
                    {engagement.executionPlan?.financialProjections?.description ? (
                    <Typography as="p" className="text-sm text-tertiary-600 leading-relaxed">
                      {engagement.executionPlan?.financialProjections.description}
                      </Typography>
                    ) : (
                    <Typography as="p" className="text-sm text-slate-400 italic">Not provided yet</Typography>
                    )}
                    {(engagement.executionPlan?.financialProjections?.file || engagement.executionPlan?.financialProjections?.date) && (
                      <div className="flex items-center justify-between mt-4 p-4 bg-slate-50 rounded-lg border border-slate-200">
                        {engagement.executionPlan?.financialProjections?.file && (
                          <div className="flex items-center gap-4 flex-1">
                            <div className={`w-12 h-12 ${getFileIcon(engagement.executionPlan.financialProjections.file.type || "mp4").bg} rounded flex items-center justify-center flex-shrink-0`}>
                              <Typography
                                as="span"
                                className="text-xs font-semibold text-white"
                              >
                                {getFileIcon(engagement.executionPlan.financialProjections.file.type || "mp4").text}
                              </Typography>
                            </div>
                            <div className="flex flex-col">
                              <Typography
                                as="p"
                                className="text-sm text-primary-900 font-medium"
                              >
                                {engagement.executionPlan.financialProjections.file.name || "Dashboard prototype recording.mp4"}
                              </Typography>
                              <Typography
                                as="p"
                                className="text-xs text-tertiary-500"
                              >
                                {formatFileSize(engagement.executionPlan.financialProjections.file.size || "16 MB")}
                              </Typography>
                            </div>
                          </div>
                        )}
                        <div className="flex items-center gap-4">
                          <Typography
                            as="p"
                            className="text-sm font-medium text-primary-900"
                          >
                            Financial Projections
                          </Typography>
                          <Typography
                            as="p"
                            className="text-sm text-tertiary-600"
                          >
                            {engagement.executionPlan?.financialProjections?.date ? formatDate(engagement.executionPlan.financialProjections.date) : null}
                          </Typography>
                          {engagement.executionPlan?.financialProjections?.file?.url && (
                            <Button
                              variant="secondary"
                              size="small"
                              onClick={() => {
                                window.open(engagement.executionPlan.financialProjections.file.url, '_blank');
                              }}
                              className="text-purple-600 hover:text-purple-700 border-purple-200 hover:border-purple-300"
                            >
                              View
                            </Button>
                          )}
                        </div>
                      </div>
                    )}
                  </div>

                  {/* Micro-Grant Proposal */}
                  <div className="flex flex-col gap-1.5">
                    <Typography
                      as="h3"
                      className="text-base font-semibold text-primary-900"
                    >
                      Micro-Grant Proposal
                    </Typography>
                    {engagement.executionPlan?.microGrantProposal?.description ? (
                    <Typography as="p" className="text-sm text-tertiary-600 leading-relaxed">
                      {engagement.executionPlan?.microGrantProposal.description}
                      </Typography>
                    ) : (
                    <Typography as="p" className="text-sm text-slate-400 italic">Not provided yet</Typography>
                    )}
                    {(engagement.executionPlan?.microGrantProposal?.file || engagement.executionPlan?.microGrantProposal?.date) && (
                      <div className="flex items-center justify-between mt-4 p-4 bg-slate-50 rounded-lg border border-slate-200">
                        {engagement.executionPlan?.microGrantProposal?.file && (
                          <div className="flex items-center gap-4 flex-1">
                            <div className={`w-12 h-12 ${getFileIcon(engagement.executionPlan.microGrantProposal.file.type || "mp4").bg} rounded flex items-center justify-center flex-shrink-0`}>
                              <Typography
                                as="span"
                                className="text-xs font-semibold text-white"
                              >
                                {getFileIcon(engagement.executionPlan.microGrantProposal.file.type || "mp4").text}
                              </Typography>
                            </div>
                            <div className="flex flex-col">
                              <Typography
                                as="p"
                                className="text-sm text-primary-900 font-medium"
                              >
                                {engagement.executionPlan.microGrantProposal.file.name || "Dashboard prototype recording.mp4"}
                              </Typography>
                              <Typography
                                as="p"
                                className="text-xs text-tertiary-500"
                              >
                                {formatFileSize(engagement.executionPlan.microGrantProposal.file.size || "16 MB")}
                              </Typography>
                            </div>
                          </div>
                        )}
                        <div className="flex items-center gap-4">
                          <Typography
                            as="p"
                            className="text-sm font-medium text-primary-900"
                          >
                            Micro-Grant Proposal
                          </Typography>
                          <Typography
                            as="p"
                            className="text-sm text-tertiary-600"
                          >
                            {engagement.executionPlan?.microGrantProposal?.date ? formatDate(engagement.executionPlan.microGrantProposal.date) : null}
                          </Typography>
                          {engagement.executionPlan?.microGrantProposal?.file?.url && (
                            <Button
                              variant="secondary"
                              size="small"
                              onClick={() => {
                                window.open(engagement.executionPlan.microGrantProposal.file.url, '_blank');
                              }}
                              className="text-purple-600 hover:text-purple-700 border-purple-200 hover:border-purple-300"
                            >
                              View
                            </Button>
                          )}
                        </div>
                      </div>
                    )}
                  </div>
                </div>
              </div>
            )}

            {activeTab === "attachments" && (
              <IdeaAttachmentsSection
                engagement={engagement}
                canEdit={canEditNow}
                activeStage={activeStage}
                formValues={ideaForm}
                onFieldChange={handleIdeaFormChange}
                onSave={() =>
                  handleSaveIdeaFields(getAttachmentSaveFieldKeys(activeStage), {
                    pitchExtras: activeStage >= 4,
                  })
                }
                saving={savingIdeaForm}
                hideSaveButton
              />
            )}

            {activeTab === "pitch-preparation" && canEditNow && (
              <EditablePitchPreparation
                formValues={ideaForm}
                onFieldChange={handleIdeaFormChange}
                onSave={() =>
                  handleSaveIdeaFields([], {
                    pitchExtras: true,
                  })
                }
                saving={savingIdeaForm}
                hideSaveButton
              />
            )}

            {activeTab === "pitch-preparation" && !canEditNow && (
              <div className="flex flex-col gap-6">
                {/* Top Section - Meta Information */}
                <div className="flex items-center justify-between w-full">
                  <Typography
                    as="p"
                    className="text-sm font-medium text-purple-600"
                  >
                    Submitted: {formatDate(engagement.submissionDate)}
                  </Typography>
                  <div className="flex items-center gap-3">
                    <Avatar
                      src={
                        engagement.submitter?.avatar &&
                          engagement.submitter.avatar.length >= 10 &&
                          (engagement.submitter.avatar.startsWith("http") ||
                            engagement.submitter.avatar.startsWith("data:image") ||
                            engagement.submitter.avatar.startsWith("/"))
                          ? engagement.submitter.avatar
                          : null
                      }
                      firstName={engagement.submitter?.name?.split(" ")[0]}
                      lastName={engagement.submitter?.name?.split(" ").slice(1).join(" ")}
                      email={engagement.submitter?.email}
                      alt={engagement.submitter?.name}
                      size="w-8 h-8"
                    />
                    <Typography
                      as="p"
                      className="text-sm font-medium text-primary-900"
                    >
                      {formatSubmitterDisplayName(engagement.submitter) || "Student"}
                    </Typography>
                  </div>
                </div>

                {/* Pitch Preparation Checklist */}
                <div className="flex flex-col gap-6">
                  <Typography
                    as="h3"
                    className="text-base font-semibold text-primary-900"
                  >
                    Preparation Checklist
                  </Typography>

                  {/* Investor Pitch Section */}
                  <div className="flex flex-col gap-4">
                    <Typography
                      as="h4"
                      className="text-xs font-bold uppercase tracking-wide text-slate-500"
                    >
                      Investor Pitch
                    </Typography>
                    <div className="flex flex-col gap-3">
                      <label className="flex items-center gap-3 cursor-pointer">
                        <input
                          type="checkbox"
                          checked={engagement.pitchPreparation?.investorPitch?.problemStatement || false}
                          readOnly
                          className="w-5 h-5 rounded border-slate-300 text-purple-600 focus:ring-purple-500"
                        />
                        <Typography as="p" className="text-sm text-primary-900">
                          Problem Statement
                        </Typography>
                      </label>
                      <label className="flex items-center gap-3 cursor-pointer">
                        <input
                          type="checkbox"
                          checked={engagement.pitchPreparation?.investorPitch?.productMarketFit || false}
                          readOnly
                          className="w-5 h-5 rounded border-slate-300 text-purple-600 focus:ring-purple-500"
                        />
                        <Typography as="p" className="text-sm text-primary-900">
                          Product Market Fit
                        </Typography>
                      </label>
                      <label className="flex items-center gap-3 cursor-pointer">
                        <input
                          type="checkbox"
                          checked={engagement.pitchPreparation?.investorPitch?.customerProfile || false}
                          readOnly
                          className="w-5 h-5 rounded border-slate-300 text-purple-600 focus:ring-purple-500"
                        />
                        <Typography as="p" className="text-sm text-primary-900">
                          Customer Profile
                        </Typography>
                      </label>
                      <label className="flex items-center gap-3 cursor-pointer">
                        <input
                          type="checkbox"
                          checked={engagement.pitchPreparation?.investorPitch?.competitiveLandscape || false}
                          readOnly
                          className="w-5 h-5 rounded border-slate-300 text-purple-600 focus:ring-purple-500"
                        />
                        <Typography as="p" className="text-sm text-primary-900">
                          Competitive Landscape
                        </Typography>
                      </label>
                      <label className="flex items-center gap-3 cursor-pointer">
                        <input
                          type="checkbox"
                          checked={engagement.pitchPreparation?.investorPitch?.targetAddressableMarket || false}
                          readOnly
                          className="w-5 h-5 rounded border-slate-300 text-purple-600 focus:ring-purple-500"
                        />
                        <Typography as="p" className="text-sm text-primary-900">
                          Target Addressable Market
                        </Typography>
                      </label>
                    </div>
                  </div>

                  {/* Market Traction Section */}
                  <div className="flex flex-col gap-4">
                    <Typography
                      as="h4"
                      className="text-xs font-bold uppercase tracking-wide text-slate-500"
                    >
                      Market Traction
                    </Typography>
                    <div className="flex flex-col gap-3">
                      <label className="flex items-center gap-3 cursor-pointer">
                        <input
                          type="checkbox"
                          checked={engagement.pitchPreparation?.marketTraction?.revenue || false}
                          readOnly
                          className="w-5 h-5 rounded border-slate-300 text-purple-600 focus:ring-purple-500"
                        />
                        <Typography as="p" className="text-sm text-primary-900">
                          Revenue
                        </Typography>
                      </label>
                      <label className="flex items-center gap-3 cursor-pointer">
                        <input
                          type="checkbox"
                          checked={engagement.pitchPreparation?.marketTraction?.margin || false}
                          readOnly
                          className="w-5 h-5 rounded border-slate-300 text-purple-600 focus:ring-purple-500"
                        />
                        <Typography as="p" className="text-sm text-primary-900">
                          Margin
                        </Typography>
                      </label>
                    </div>
                  </div>

                  {/* Metrics Section */}
                  <div className="flex flex-col gap-4">
                    <Typography
                      as="h4"
                      className="text-xs font-bold uppercase tracking-wide text-slate-500"
                    >
                      Metrics
                    </Typography>
                    <div className="flex flex-col gap-3">
                      <label className="flex items-center gap-3 cursor-pointer">
                        <input
                          type="checkbox"
                          checked={engagement.pitchPreparation?.metrics?.sales || false}
                          readOnly
                          className="w-5 h-5 rounded border-slate-300 text-purple-600 focus:ring-purple-500"
                        />
                        <Typography as="p" className="text-sm text-primary-900">
                          Sales
                        </Typography>
                      </label>
                      <label className="flex items-center gap-3 cursor-pointer">
                        <input
                          type="checkbox"
                          checked={engagement.pitchPreparation?.metrics?.pipeline || false}
                          readOnly
                          className="w-5 h-5 rounded border-slate-300 text-purple-600 focus:ring-purple-500"
                        />
                        <Typography as="p" className="text-sm text-primary-900">
                          Pipeline
                        </Typography>
                      </label>
                      <label className="flex items-center gap-3 cursor-pointer">
                        <input
                          type="checkbox"
                          checked={engagement.pitchPreparation?.metrics?.marketing || false}
                          readOnly
                          className="w-5 h-5 rounded border-slate-300 text-purple-600 focus:ring-purple-500"
                        />
                        <Typography as="p" className="text-sm text-primary-900">
                          Marketing
                        </Typography>
                      </label>
                      <label className="flex items-center gap-3 cursor-pointer">
                        <input
                          type="checkbox"
                          checked={engagement.pitchPreparation?.metrics?.followers || false}
                          readOnly
                          className="w-5 h-5 rounded border-slate-300 text-purple-600 focus:ring-purple-500"
                        />
                        <Typography as="p" className="text-sm text-primary-900">
                          Followers
                        </Typography>
                      </label>
                      <label className="flex items-center gap-3 cursor-pointer">
                        <input
                          type="checkbox"
                          checked={engagement.pitchPreparation?.metrics?.customerAcquisitionCost || false}
                          readOnly
                          className="w-5 h-5 rounded border-slate-300 text-purple-600 focus:ring-purple-500"
                        />
                        <Typography as="p" className="text-sm text-primary-900">
                          Customer Acquisition Cost
                        </Typography>
                      </label>
                    </div>
                  </div>

                  {/* Additional Items */}
                  <div className="flex flex-col gap-4">
                    <div className="flex flex-col gap-3">
                      <label className="flex items-center gap-3 cursor-pointer">
                        <input
                          type="checkbox"
                          checked={!!engagement.pitchPreparation?.additional?.productVideoLink}
                          readOnly
                          className="w-5 h-5 rounded border-slate-300 text-purple-600 focus:ring-purple-500"
                        />
                        <Typography as="p" className="text-sm text-primary-900">
                          Link to product/video
                        </Typography>
                        {engagement.pitchPreparation?.additional?.productVideoLink && (
                          <a
                            href={engagement.pitchPreparation.additional.productVideoLink}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="text-sm text-blue-600 hover:text-blue-700 underline ml-2"
                          >
                            {engagement.pitchPreparation.additional.productVideoLink}
                          </a>
                        )}
                      </label>
                      <label className="flex items-center gap-3 cursor-pointer">
                        <input
                          type="checkbox"
                          checked={!!engagement.pitchPreparation?.additional?.pitchDeckUpload}
                          readOnly
                          className="w-5 h-5 rounded border-slate-300 text-purple-600 focus:ring-purple-500"
                        />
                        <Typography as="p" className="text-sm text-primary-900">
                          Pitch deck upload
                        </Typography>
                        {engagement.pitchPreparation?.additional?.pitchDeckUpload && (
                          <div className="ml-2 flex items-center gap-2">
                            <Icon icon="lucide:file" className="h-4 w-4 text-slate-600" />
                            <Typography as="p" className="text-sm text-slate-600">
                              {engagement.pitchPreparation.additional.pitchDeckUpload.name || "Pitch Deck"}
                            </Typography>
                          </div>
                        )}
                      </label>
                    </div>
                  </div>
                </div>
              </div>
            )}

            {activeTab === "growth-strategy" && canEditNow && (
              <EditableStageFieldsPanel
                title="Growth strategy"
                subtitle="Business model, readiness, GTM, and customer feedback."
                fields={GROWTH_STRATEGY_FIELDS}
                formValues={ideaForm}
                onFieldChange={handleIdeaFormChange}
                onSave={() => handleSaveIdeaFields(keysFromFieldConfig(GROWTH_STRATEGY_FIELDS))}
                saving={savingIdeaForm}
                accentClass="bg-blue-600 hover:bg-blue-700"
                hideSaveButton
              />
            )}

            {activeTab === "growth-strategy" && !canEditNow && (
              <div className="flex flex-col gap-4 text-start">
                {/* Top Section - Meta Information */}
                <div className="flex items-center justify-between w-full">
                  <Typography
                    as="p"
                    className="text-sm font-medium text-purple-600"
                  >
                    Submitted: {formatDate(engagement.submissionDate)}
                  </Typography>
                  <div className="flex items-center gap-3">
                    <Avatar
                      src={
                        engagement.submitter?.avatar &&
                          engagement.submitter.avatar.length >= 10 &&
                          (engagement.submitter.avatar.startsWith("http") ||
                            engagement.submitter.avatar.startsWith("data:image") ||
                            engagement.submitter.avatar.startsWith("/"))
                          ? engagement.submitter.avatar
                          : null
                      }
                      firstName={engagement.submitter?.name?.split(" ")[0]}
                      lastName={engagement.submitter?.name?.split(" ").slice(1).join(" ")}
                      email={engagement.submitter?.email}
                      alt={engagement.submitter?.name}
                      size="w-8 h-8"
                    />
                    <Typography
                      as="p"
                      className="text-sm font-medium text-primary-900"
                    >
                      {formatSubmitterDisplayName(engagement.submitter) || "Student"}
                    </Typography>
                  </div>
                </div>

                {/* Growth Strategy Sections */}
                <div className="flex flex-col gap-4 text-start">
                  {/* Enhance Business Model */}
                  <div className="flex flex-col gap-1.5">
                    <Typography
                      as="h3"
                      className="text-base font-semibold text-primary-900"
                    >
                      Enhance Business Model
                    </Typography>
                    {engagement.growthStrategy?.enhanceBusinessModel?.description ? (
                    <Typography as="p" className="text-sm text-tertiary-600 leading-relaxed">
                      {engagement.growthStrategy?.enhanceBusinessModel.description}
                      </Typography>
                    ) : (
                    <Typography as="p" className="text-sm text-slate-400 italic">Not provided yet</Typography>
                    )}
                    {(engagement.growthStrategy?.enhanceBusinessModel?.file || engagement.growthStrategy?.enhanceBusinessModel?.date) && (
                      <div className="flex items-center justify-between mt-4 p-4 bg-slate-50 rounded-lg border border-slate-200">
                        {engagement.growthStrategy?.enhanceBusinessModel?.file && (
                          <div className="flex items-center gap-4 flex-1">
                            <div className={`w-12 h-12 ${getFileIcon(engagement.growthStrategy.enhanceBusinessModel.file.type || "mp4").bg} rounded flex items-center justify-center flex-shrink-0`}>
                              <Typography
                                as="span"
                                className="text-xs font-semibold text-white"
                              >
                                {getFileIcon(engagement.growthStrategy.enhanceBusinessModel.file.type || "mp4").text}
                              </Typography>
                            </div>
                            <div className="flex flex-col">
                              <Typography
                                as="p"
                                className="text-sm text-primary-900 font-medium"
                              >
                                {engagement.growthStrategy.enhanceBusinessModel.file.name || "Dashboard prototype recording.mp4"}
                              </Typography>
                              <Typography
                                as="p"
                                className="text-xs text-tertiary-500"
                              >
                                {formatFileSize(engagement.growthStrategy.enhanceBusinessModel.file.size || "16 MB")}
                              </Typography>
                            </div>
                          </div>
                        )}
                        <div className="flex items-center gap-4">
                          <Typography
                            as="p"
                            className="text-sm font-medium text-primary-900"
                          >
                            {engagement.growthStrategy?.enhanceBusinessModel?.category || "MVP"}
                          </Typography>
                          <Typography
                            as="p"
                            className="text-sm text-tertiary-600"
                          >
                            {engagement.growthStrategy?.enhanceBusinessModel?.date ? formatDate(engagement.growthStrategy.enhanceBusinessModel.date) : null}
                          </Typography>
                          {engagement.growthStrategy?.enhanceBusinessModel?.file?.url && (
                            <Button
                              variant="secondary"
                              size="small"
                              onClick={() => {
                                window.open(engagement.growthStrategy.enhanceBusinessModel.file.url, '_blank');
                              }}
                              className="text-purple-600 hover:text-purple-700 border-purple-200 hover:border-purple-300"
                            >
                              View
                            </Button>
                          )}
                        </div>
                      </div>
                    )}
                  </div>

                  {/* Product Readiness */}
                  <div className="flex flex-col gap-1.5">
                    <Typography
                      as="h3"
                      className="text-base font-semibold text-primary-900"
                    >
                      Product Readiness
                    </Typography>
                    {engagement.growthStrategy?.productReadiness?.description ? (
                    <Typography as="p" className="text-sm text-tertiary-600 leading-relaxed">
                      {engagement.growthStrategy?.productReadiness.description}
                      </Typography>
                    ) : (
                    <Typography as="p" className="text-sm text-slate-400 italic">Not provided yet</Typography>
                    )}
                    {engagement.growthStrategy?.productReadiness?.url && (
                      <div className="mt-2">
                        <a
                          href={engagement.growthStrategy.productReadiness.url}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="text-sm font-medium text-blue-600 hover:text-blue-700 underline"
                        >
                          {engagement.growthStrategy.productReadiness.url}
                        </a>
                      </div>
                    )}
                    {(engagement.growthStrategy?.productReadiness?.file || engagement.growthStrategy?.productReadiness?.date) && (
                      <div className="flex items-center justify-between mt-4 p-4 bg-slate-50 rounded-lg border border-slate-200">
                        {engagement.growthStrategy?.productReadiness?.file && (
                          <div className="flex items-center gap-4 flex-1">
                            <div className={`w-12 h-12 ${getFileIcon(engagement.growthStrategy.productReadiness.file.type || "mp4").bg} rounded flex items-center justify-center flex-shrink-0`}>
                              <Typography
                                as="span"
                                className="text-xs font-semibold text-white"
                              >
                                {getFileIcon(engagement.growthStrategy.productReadiness.file.type || "mp4").text}
                              </Typography>
                            </div>
                            <div className="flex flex-col">
                              <Typography
                                as="p"
                                className="text-sm text-primary-900 font-medium"
                              >
                                {engagement.growthStrategy.productReadiness.file.name || "Dashboard prototype recording.mp4"}
                              </Typography>
                              <Typography
                                as="p"
                                className="text-xs text-tertiary-500"
                              >
                                {formatFileSize(engagement.growthStrategy.productReadiness.file.size || "16 MB")}
                              </Typography>
                            </div>
                          </div>
                        )}
                        <div className="flex items-center gap-4">
                          <Typography
                            as="p"
                            className="text-sm font-medium text-primary-900"
                          >
                            {engagement.growthStrategy?.productReadiness?.category || "Market Validation"}
                          </Typography>
                          <Typography
                            as="p"
                            className="text-sm text-tertiary-600"
                          >
                            {engagement.growthStrategy?.productReadiness?.date ? formatDate(engagement.growthStrategy.productReadiness.date) : null}
                          </Typography>
                          {engagement.growthStrategy?.productReadiness?.file?.url && (
                            <Button
                              variant="secondary"
                              size="small"
                              onClick={() => {
                                window.open(engagement.growthStrategy.productReadiness.file.url, '_blank');
                              }}
                              className="text-purple-600 hover:text-purple-700 border-purple-200 hover:border-purple-300"
                            >
                              View
                            </Button>
                          )}
                        </div>
                      </div>
                    )}
                  </div>

                  {/* Customer Profile */}
                  <div className="flex flex-col gap-1.5">
                    <Typography
                      as="h3"
                      className="text-base font-semibold text-primary-900"
                    >
                      Customer Profile
                    </Typography>
                    {engagement.growthStrategy?.customerProfile?.description ? (
                    <Typography as="p" className="text-sm text-tertiary-600 leading-relaxed">
                      {engagement.growthStrategy?.customerProfile.description}
                      </Typography>
                    ) : (
                    <Typography as="p" className="text-sm text-slate-400 italic">Not provided yet</Typography>
                    )}
                    {(engagement.growthStrategy?.customerProfile?.file || engagement.growthStrategy?.customerProfile?.date) && (
                      <div className="flex items-center justify-between mt-4 p-4 bg-slate-50 rounded-lg border border-slate-200">
                        {engagement.growthStrategy?.customerProfile?.file && (
                          <div className="flex items-center gap-4 flex-1">
                            <div className={`w-12 h-12 ${getFileIcon(engagement.growthStrategy.customerProfile.file.type || "mp4").bg} rounded flex items-center justify-center flex-shrink-0`}>
                              <Typography
                                as="span"
                                className="text-xs font-semibold text-white"
                              >
                                {getFileIcon(engagement.growthStrategy.customerProfile.file.type || "mp4").text}
                              </Typography>
                            </div>
                            <div className="flex flex-col">
                              <Typography
                                as="p"
                                className="text-sm text-primary-900 font-medium"
                              >
                                {engagement.growthStrategy.customerProfile.file.name || "Dashboard prototype recording.mp4"}
                              </Typography>
                              <Typography
                                as="p"
                                className="text-xs text-tertiary-500"
                              >
                                {formatFileSize(engagement.growthStrategy.customerProfile.file.size || "16 MB")}
                              </Typography>
                            </div>
                          </div>
                        )}
                        <div className="flex items-center gap-4">
                          <Typography
                            as="p"
                            className="text-sm font-medium text-primary-900"
                          >
                            {engagement.growthStrategy?.customerProfile?.category || "Financial Projections"}
                          </Typography>
                          <Typography
                            as="p"
                            className="text-sm text-tertiary-600"
                          >
                            {engagement.growthStrategy?.customerProfile?.date ? formatDate(engagement.growthStrategy.customerProfile.date) : null}
                          </Typography>
                          {engagement.growthStrategy?.customerProfile?.file?.url && (
                            <Button
                              variant="secondary"
                              size="small"
                              onClick={() => {
                                window.open(engagement.growthStrategy.customerProfile.file.url, '_blank');
                              }}
                              className="text-purple-600 hover:text-purple-700 border-purple-200 hover:border-purple-300"
                            >
                              View
                            </Button>
                          )}
                        </div>
                      </div>
                    )}
                  </div>

                  {/* GTM Strategy */}
                  <div className="flex flex-col gap-1.5">
                    <Typography
                      as="h3"
                      className="text-base font-semibold text-primary-900"
                    >
                      GTM Strategy
                    </Typography>
                    {engagement.growthStrategy?.gtmStrategy?.description ? (
                    <Typography as="p" className="text-sm text-tertiary-600 leading-relaxed">
                      {engagement.growthStrategy?.gtmStrategy.description}
                      </Typography>
                    ) : (
                    <Typography as="p" className="text-sm text-slate-400 italic">Not provided yet</Typography>
                    )}
                    {(engagement.growthStrategy?.gtmStrategy?.file || engagement.growthStrategy?.gtmStrategy?.date) && (
                      <div className="flex items-center justify-between mt-4 p-4 bg-slate-50 rounded-lg border border-slate-200">
                        {engagement.growthStrategy?.gtmStrategy?.file && (
                          <div className="flex items-center gap-4 flex-1">
                            <div className={`w-12 h-12 ${getFileIcon(engagement.growthStrategy.gtmStrategy.file.type || "mp4").bg} rounded flex items-center justify-center flex-shrink-0`}>
                              <Typography
                                as="span"
                                className="text-xs font-semibold text-white"
                              >
                                {getFileIcon(engagement.growthStrategy.gtmStrategy.file.type || "mp4").text}
                              </Typography>
                            </div>
                            <div className="flex flex-col">
                              <Typography
                                as="p"
                                className="text-sm text-primary-900 font-medium"
                              >
                                {engagement.growthStrategy.gtmStrategy.file.name || "Dashboard prototype recording.mp4"}
                              </Typography>
                              <Typography
                                as="p"
                                className="text-xs text-tertiary-500"
                              >
                                {formatFileSize(engagement.growthStrategy.gtmStrategy.file.size || "16 MB")}
                              </Typography>
                            </div>
                          </div>
                        )}
                        <div className="flex items-center gap-4">
                          <Typography
                            as="p"
                            className="text-sm font-medium text-primary-900"
                          >
                            {engagement.growthStrategy?.gtmStrategy?.category || "Micro-Grant Proposal"}
                          </Typography>
                          <Typography
                            as="p"
                            className="text-sm text-tertiary-600"
                          >
                            {engagement.growthStrategy?.gtmStrategy?.date ? formatDate(engagement.growthStrategy.gtmStrategy.date) : null}
                          </Typography>
                          {engagement.growthStrategy?.gtmStrategy?.file?.url && (
                            <Button
                              variant="secondary"
                              size="small"
                              onClick={() => {
                                window.open(engagement.growthStrategy.gtmStrategy.file.url, '_blank');
                              }}
                              className="text-purple-600 hover:text-purple-700 border-purple-200 hover:border-purple-300"
                            >
                              View
                            </Button>
                          )}
                        </div>
                      </div>
                    )}
                  </div>

                  {/* Product Market Fit */}
                  <div className="flex flex-col gap-1.5">
                    <Typography
                      as="h3"
                      className="text-base font-semibold text-primary-900"
                    >
                      Product Market Fit
                    </Typography>
                    {engagement.growthStrategy?.productMarketFit?.description ? (
                    <Typography as="p" className="text-sm text-tertiary-600 leading-relaxed">
                      {engagement.growthStrategy?.productMarketFit.description}
                      </Typography>
                    ) : (
                    <Typography as="p" className="text-sm text-slate-400 italic">Not provided yet</Typography>
                    )}
                    {(engagement.growthStrategy?.productMarketFit?.file || engagement.growthStrategy?.productMarketFit?.date) && (
                      <div className="flex items-center justify-between mt-4 p-4 bg-slate-50 rounded-lg border border-slate-200">
                        {engagement.growthStrategy?.productMarketFit?.file && (
                          <div className="flex items-center gap-4 flex-1">
                            <div className={`w-12 h-12 ${getFileIcon(engagement.growthStrategy.productMarketFit.file.type || "mp4").bg} rounded flex items-center justify-center flex-shrink-0`}>
                              <Typography
                                as="span"
                                className="text-xs font-semibold text-white"
                              >
                                {getFileIcon(engagement.growthStrategy.productMarketFit.file.type || "mp4").text}
                              </Typography>
                            </div>
                            <div className="flex flex-col">
                              <Typography
                                as="p"
                                className="text-sm text-primary-900 font-medium"
                              >
                                {engagement.growthStrategy.productMarketFit.file.name || "Dashboard prototype recording.mp4"}
                              </Typography>
                              <Typography
                                as="p"
                                className="text-xs text-tertiary-500"
                              >
                                {formatFileSize(engagement.growthStrategy.productMarketFit.file.size || "16 MB")}
                              </Typography>
                            </div>
                          </div>
                        )}
                        <div className="flex items-center gap-4">
                          <Typography
                            as="p"
                            className="text-sm font-medium text-primary-900"
                          >
                            {engagement.growthStrategy?.productMarketFit?.category || "Micro-Grant Proposal"}
                          </Typography>
                          <Typography
                            as="p"
                            className="text-sm text-tertiary-600"
                          >
                            {engagement.growthStrategy?.productMarketFit?.date ? formatDate(engagement.growthStrategy.productMarketFit.date) : null}
                          </Typography>
                          {engagement.growthStrategy?.productMarketFit?.file?.url && (
                            <Button
                              variant="secondary"
                              size="small"
                              onClick={() => {
                                window.open(engagement.growthStrategy.productMarketFit.file.url, '_blank');
                              }}
                              className="text-purple-600 hover:text-purple-700 border-purple-200 hover:border-purple-300"
                            >
                              View
                            </Button>
                          )}
                        </div>
                      </div>
                    )}
                  </div>

                  {/* Target Addressable Market */}
                  <div className="flex flex-col gap-1.5">
                    <Typography
                      as="h3"
                      className="text-base font-semibold text-primary-900"
                    >
                      Target Addressable Market
                    </Typography>
                    {engagement.growthStrategy?.targetAddressableMarket?.description ? (
                    <Typography as="p" className="text-sm text-tertiary-600 leading-relaxed">
                      {engagement.growthStrategy?.targetAddressableMarket.description}
                      </Typography>
                    ) : (
                    <Typography as="p" className="text-sm text-slate-400 italic">Not provided yet</Typography>
                    )}
                    {(engagement.growthStrategy?.targetAddressableMarket?.file || engagement.growthStrategy?.targetAddressableMarket?.date) && (
                      <div className="flex items-center justify-between mt-4 p-4 bg-slate-50 rounded-lg border border-slate-200">
                        {engagement.growthStrategy?.targetAddressableMarket?.file && (
                          <div className="flex items-center gap-4 flex-1">
                            <div className={`w-12 h-12 ${getFileIcon(engagement.growthStrategy.targetAddressableMarket.file.type || "mp4").bg} rounded flex items-center justify-center flex-shrink-0`}>
                              <Typography
                                as="span"
                                className="text-xs font-semibold text-white"
                              >
                                {getFileIcon(engagement.growthStrategy.targetAddressableMarket.file.type || "mp4").text}
                              </Typography>
                            </div>
                            <div className="flex flex-col">
                              <Typography
                                as="p"
                                className="text-sm text-primary-900 font-medium"
                              >
                                {engagement.growthStrategy.targetAddressableMarket.file.name || "Dashboard prototype recording.mp4"}
                              </Typography>
                              <Typography
                                as="p"
                                className="text-xs text-tertiary-500"
                              >
                                {formatFileSize(engagement.growthStrategy.targetAddressableMarket.file.size || "16 MB")}
                              </Typography>
                            </div>
                          </div>
                        )}
                        <div className="flex items-center gap-4">
                          <Typography
                            as="p"
                            className="text-sm font-medium text-primary-900"
                          >
                            {engagement.growthStrategy?.targetAddressableMarket?.category || "Micro-Grant Proposal"}
                          </Typography>
                          <Typography
                            as="p"
                            className="text-sm text-tertiary-600"
                          >
                            {engagement.growthStrategy?.targetAddressableMarket?.date ? formatDate(engagement.growthStrategy.targetAddressableMarket.date) : null}
                          </Typography>
                          {engagement.growthStrategy?.targetAddressableMarket?.file?.url && (
                            <Button
                              variant="secondary"
                              size="small"
                              onClick={() => {
                                window.open(engagement.growthStrategy.targetAddressableMarket.file.url, '_blank');
                              }}
                              className="text-purple-600 hover:text-purple-700 border-purple-200 hover:border-purple-300"
                            >
                              View
                            </Button>
                          )}
                        </div>
                      </div>
                    )}
                  </div>

                  {/* Customer Feedback */}
                  <div className="flex flex-col gap-1.5">
                    <Typography
                      as="h3"
                      className="text-base font-semibold text-primary-900"
                    >
                      Customer Feedback
                    </Typography>
                    {engagement.growthStrategy?.customerFeedback?.description ? (
                    <Typography as="p" className="text-sm text-tertiary-600 leading-relaxed">
                      {engagement.growthStrategy?.customerFeedback.description}
                      </Typography>
                    ) : (
                    <Typography as="p" className="text-sm text-slate-400 italic">Not provided yet</Typography>
                    )}
                    {(engagement.growthStrategy?.customerFeedback?.file || engagement.growthStrategy?.customerFeedback?.date) && (
                      <div className="flex items-center justify-between mt-4 p-4 bg-slate-50 rounded-lg border border-slate-200">
                        {engagement.growthStrategy?.customerFeedback?.file && (
                          <div className="flex items-center gap-4 flex-1">
                            <div className={`w-12 h-12 ${getFileIcon(engagement.growthStrategy.customerFeedback.file.type || "mp4").bg} rounded flex items-center justify-center flex-shrink-0`}>
                              <Typography
                                as="span"
                                className="text-xs font-semibold text-white"
                              >
                                {getFileIcon(engagement.growthStrategy.customerFeedback.file.type || "mp4").text}
                              </Typography>
                            </div>
                            <div className="flex flex-col">
                              <Typography
                                as="p"
                                className="text-sm text-primary-900 font-medium"
                              >
                                {engagement.growthStrategy.customerFeedback.file.name || "Dashboard prototype recording.mp4"}
                              </Typography>
                              <Typography
                                as="p"
                                className="text-xs text-tertiary-500"
                              >
                                {formatFileSize(engagement.growthStrategy.customerFeedback.file.size || "16 MB")}
                              </Typography>
                            </div>
                          </div>
                        )}
                        <div className="flex items-center gap-4">
                          <Typography
                            as="p"
                            className="text-sm font-medium text-primary-900"
                          >
                            {engagement.growthStrategy?.customerFeedback?.category || "Micro-Grant Proposal"}
                          </Typography>
                          <Typography
                            as="p"
                            className="text-sm text-tertiary-600"
                          >
                            {engagement.growthStrategy?.customerFeedback?.date ? formatDate(engagement.growthStrategy.customerFeedback.date) : null}
                          </Typography>
                          {engagement.growthStrategy?.customerFeedback?.file?.url && (
                            <Button
                              variant="secondary"
                              size="small"
                              onClick={() => {
                                window.open(engagement.growthStrategy.customerFeedback.file.url, '_blank');
                              }}
                              className="text-purple-600 hover:text-purple-700 border-purple-200 hover:border-purple-300"
                            >
                              View
                            </Button>
                          )}
                        </div>
                      </div>
                    )}
                  </div>
                </div>
              </div>
            )}

            {activeTab === "market-strategy" && canEditNow && (
              <EditableStageFieldsPanel
                title="Market strategy"
                subtitle="Customer, value, fit, and market sizing."
                fields={MARKET_STRATEGY_FIELDS}
                formValues={ideaForm}
                onFieldChange={handleIdeaFormChange}
                onSave={() => handleSaveIdeaFields(keysFromFieldConfig(MARKET_STRATEGY_FIELDS))}
                saving={savingIdeaForm}
                hideSaveButton
              />
            )}

            {activeTab === "market-strategy" && !canEditNow && (
              <div className="flex flex-col gap-4 text-start">
                {/* Top Section - Meta Information */}
                <div className="flex items-center justify-between w-full">
                  <Typography
                    as="p"
                    className="text-sm font-medium text-purple-600"
                  >
                    Submitted: {formatDate(engagement.submissionDate)}
                  </Typography>
                  <div className="flex items-center gap-3">
                    <Avatar
                      src={
                        engagement.submitter?.avatar &&
                          engagement.submitter.avatar.length >= 10 &&
                          (engagement.submitter.avatar.startsWith("http") ||
                            engagement.submitter.avatar.startsWith("data:image") ||
                            engagement.submitter.avatar.startsWith("/"))
                          ? engagement.submitter.avatar
                          : null
                      }
                      firstName={engagement.submitter?.name?.split(" ")[0]}
                      lastName={engagement.submitter?.name?.split(" ").slice(1).join(" ")}
                      email={engagement.submitter?.email}
                      alt={engagement.submitter?.name}
                      size="w-8 h-8"
                    />
                    <Typography
                      as="p"
                      className="text-sm font-medium text-primary-900"
                    >
                      {formatSubmitterDisplayName(engagement.submitter) || "Student"}
                    </Typography>
                  </div>
                </div>

                {/* Market Strategy Sections */}
                <div className="flex flex-col gap-4 text-start">
                  {/* Ideal Customer Profile */}
                  <div className="flex flex-col gap-1.5">
                    <Typography
                      as="h3"
                      className="text-base font-semibold text-primary-900"
                    >
                      Ideal Customer Profile
                    </Typography>
                    {engagement.marketStrategy?.idealCustomerProfile?.description ? (
                    <Typography as="p" className="text-sm text-tertiary-600 leading-relaxed">
                      {engagement.marketStrategy?.idealCustomerProfile.description}
                      </Typography>
                    ) : (
                    <Typography as="p" className="text-sm text-slate-400 italic">Not provided yet</Typography>
                    )}
                    {(engagement.marketStrategy?.idealCustomerProfile?.file || engagement.marketStrategy?.idealCustomerProfile?.date) && (
                      <div className="flex items-center justify-between mt-4 p-4 bg-slate-50 rounded-lg border border-slate-200">
                        {engagement.marketStrategy?.idealCustomerProfile?.file && (
                          <div className="flex items-center gap-4 flex-1">
                            <div className={`w-12 h-12 ${getFileIcon(engagement.marketStrategy.idealCustomerProfile.file.type || "mp4").bg} rounded flex items-center justify-center flex-shrink-0`}>
                              <Typography
                                as="span"
                                className="text-xs font-semibold text-white"
                              >
                                {getFileIcon(engagement.marketStrategy.idealCustomerProfile.file.type || "mp4").text}
                              </Typography>
                            </div>
                            <div className="flex flex-col">
                              <Typography
                                as="p"
                                className="text-sm text-primary-900 font-medium"
                              >
                                {engagement.marketStrategy.idealCustomerProfile.file.name || "Dashboard prototype recording.mp4"}
                              </Typography>
                              <Typography
                                as="p"
                                className="text-xs text-tertiary-500"
                              >
                                {formatFileSize(engagement.marketStrategy.idealCustomerProfile.file.size || "16 MB")}
                              </Typography>
                            </div>
                          </div>
                        )}
                        <div className="flex items-center gap-4">
                          <a
                            href={engagement.marketStrategy?.idealCustomerProfile?.file?.url || "#"}
                            className="text-sm font-medium text-purple-600 hover:text-purple-700 underline"
                          >
                            Ideal Customer Profile
                          </a>
                          <Typography
                            as="p"
                            className="text-sm text-tertiary-600"
                          >
                            {engagement.marketStrategy?.idealCustomerProfile?.date ? formatDate(engagement.marketStrategy.idealCustomerProfile.date) : null}
                          </Typography>
                          {engagement.marketStrategy?.idealCustomerProfile?.file?.url && (
                            <Button
                              variant="secondary"
                              size="small"
                              onClick={() => {
                                window.open(engagement.marketStrategy.idealCustomerProfile.file.url, '_blank');
                              }}
                              className="text-purple-600 hover:text-purple-700 border-purple-200 hover:border-purple-300"
                            >
                              View
                            </Button>
                          )}
                        </div>
                      </div>
                    )}
                  </div>

                  {/* Value Proposition */}
                  <div className="flex flex-col gap-1.5">
                    <Typography
                      as="h3"
                      className="text-base font-semibold text-primary-900"
                    >
                      Value Proposition
                    </Typography>
                    {engagement.marketStrategy?.valueProposition?.description ? (
                    <Typography as="p" className="text-sm text-tertiary-600 leading-relaxed">
                      {engagement.marketStrategy?.valueProposition.description}
                      </Typography>
                    ) : (
                    <Typography as="p" className="text-sm text-slate-400 italic">Not provided yet</Typography>
                    )}
                    {(engagement.marketStrategy?.valueProposition?.file || engagement.marketStrategy?.valueProposition?.date) && (
                      <div className="flex items-center justify-between mt-4 p-4 bg-slate-50 rounded-lg border border-slate-200">
                        {engagement.marketStrategy?.valueProposition?.file && (
                          <div className="flex items-center gap-4 flex-1">
                            <div className={`w-12 h-12 ${getFileIcon(engagement.marketStrategy.valueProposition.file.type || "mp4").bg} rounded flex items-center justify-center flex-shrink-0`}>
                              <Typography
                                as="span"
                                className="text-xs font-semibold text-white"
                              >
                                {getFileIcon(engagement.marketStrategy.valueProposition.file.type || "mp4").text}
                              </Typography>
                            </div>
                            <div className="flex flex-col">
                              <Typography
                                as="p"
                                className="text-sm text-primary-900 font-medium"
                              >
                                {engagement.marketStrategy.valueProposition.file.name || "Dashboard prototype recording.mp4"}
                              </Typography>
                              <Typography
                                as="p"
                                className="text-xs text-tertiary-500"
                              >
                                {formatFileSize(engagement.marketStrategy.valueProposition.file.size || "16 MB")}
                              </Typography>
                            </div>
                          </div>
                        )}
                        <div className="flex items-center gap-4">
                          <a
                            href={engagement.marketStrategy?.valueProposition?.file?.url || "#"}
                            className="text-sm font-medium text-purple-600 hover:text-purple-700 underline"
                          >
                            Value Proposition
                          </a>
                          <Typography
                            as="p"
                            className="text-sm text-tertiary-600"
                          >
                            {engagement.marketStrategy?.valueProposition?.date ? formatDate(engagement.marketStrategy.valueProposition.date) : null}
                          </Typography>
                          {engagement.marketStrategy?.valueProposition?.file?.url && (
                            <Button
                              variant="secondary"
                              size="small"
                              onClick={() => {
                                window.open(engagement.marketStrategy.valueProposition.file.url, '_blank');
                              }}
                              className="text-purple-600 hover:text-purple-700 border-purple-200 hover:border-purple-300"
                            >
                              View
                            </Button>
                          )}
                        </div>
                      </div>
                    )}
                  </div>

                  {/* Product Market Fit */}
                  <div className="flex flex-col gap-1.5">
                    <Typography
                      as="h3"
                      className="text-base font-semibold text-primary-900"
                    >
                      Product Market Fit
                    </Typography>
                    {engagement.marketStrategy?.productMarketFit?.description ? (
                    <Typography as="p" className="text-sm text-tertiary-600 leading-relaxed">
                      {engagement.marketStrategy?.productMarketFit.description}
                      </Typography>
                    ) : (
                    <Typography as="p" className="text-sm text-slate-400 italic">Not provided yet</Typography>
                    )}
                    {(engagement.marketStrategy?.productMarketFit?.file || engagement.marketStrategy?.productMarketFit?.date) && (
                      <div className="flex items-center justify-between mt-4 p-4 bg-slate-50 rounded-lg border border-slate-200">
                        {engagement.marketStrategy?.productMarketFit?.file && (
                          <div className="flex items-center gap-4 flex-1">
                            <div className={`w-12 h-12 ${getFileIcon(engagement.marketStrategy.productMarketFit.file.type || "mp4").bg} rounded flex items-center justify-center flex-shrink-0`}>
                              <Typography
                                as="span"
                                className="text-xs font-semibold text-white"
                              >
                                {getFileIcon(engagement.marketStrategy.productMarketFit.file.type || "mp4").text}
                              </Typography>
                            </div>
                            <div className="flex flex-col">
                              <Typography
                                as="p"
                                className="text-sm text-primary-900 font-medium"
                              >
                                {engagement.marketStrategy.productMarketFit.file.name || "Dashboard prototype recording.mp4"}
                              </Typography>
                              <Typography
                                as="p"
                                className="text-xs text-tertiary-500"
                              >
                                {formatFileSize(engagement.marketStrategy.productMarketFit.file.size || "16 MB")}
                              </Typography>
                            </div>
                          </div>
                        )}
                        <div className="flex items-center gap-4">
                          <a
                            href={engagement.marketStrategy?.productMarketFit?.file?.url || "#"}
                            className="text-sm font-medium text-purple-600 hover:text-purple-700 underline"
                          >
                            Product Market Fit
                          </a>
                          <Typography
                            as="p"
                            className="text-sm text-tertiary-600"
                          >
                            {engagement.marketStrategy?.productMarketFit?.date ? formatDate(engagement.marketStrategy.productMarketFit.date) : null}
                          </Typography>
                          {engagement.marketStrategy?.productMarketFit?.file?.url && (
                            <Button
                              variant="secondary"
                              size="small"
                              onClick={() => {
                                window.open(engagement.marketStrategy.productMarketFit.file.url, '_blank');
                              }}
                              className="text-purple-600 hover:text-purple-700 border-purple-200 hover:border-purple-300"
                            >
                              View
                            </Button>
                          )}
                        </div>
                      </div>
                    )}
                  </div>

                  {/* Target Addressable Market */}
                  <div className="flex flex-col gap-1.5">
                    <Typography
                      as="h3"
                      className="text-base font-semibold text-primary-900"
                    >
                      Target Addressable Market
                    </Typography>
                    {engagement.marketStrategy?.targetAddressableMarket?.description ? (
                    <Typography as="p" className="text-sm text-tertiary-600 leading-relaxed">
                      {engagement.marketStrategy?.targetAddressableMarket.description}
                      </Typography>
                    ) : (
                    <Typography as="p" className="text-sm text-slate-400 italic">Not provided yet</Typography>
                    )}
                    {(engagement.marketStrategy?.targetAddressableMarket?.file || engagement.marketStrategy?.targetAddressableMarket?.date) && (
                      <div className="flex items-center justify-between mt-4 p-4 bg-slate-50 rounded-lg border border-slate-200">
                        {engagement.marketStrategy?.targetAddressableMarket?.file && (
                          <div className="flex items-center gap-4 flex-1">
                            <div className={`w-12 h-12 ${getFileIcon(engagement.marketStrategy.targetAddressableMarket.file.type || "mp4").bg} rounded flex items-center justify-center flex-shrink-0`}>
                              <Typography
                                as="span"
                                className="text-xs font-semibold text-white"
                              >
                                {getFileIcon(engagement.marketStrategy.targetAddressableMarket.file.type || "mp4").text}
                              </Typography>
                            </div>
                            <div className="flex flex-col">
                              <Typography
                                as="p"
                                className="text-sm text-primary-900 font-medium"
                              >
                                {engagement.marketStrategy.targetAddressableMarket.file.name || "Dashboard prototype recording.mp4"}
                              </Typography>
                              <Typography
                                as="p"
                                className="text-xs text-tertiary-500"
                              >
                                {formatFileSize(engagement.marketStrategy.targetAddressableMarket.file.size || "16 MB")}
                              </Typography>
                            </div>
                          </div>
                        )}
                        <div className="flex items-center gap-4">
                          <a
                            href={engagement.marketStrategy?.targetAddressableMarket?.file?.url || "#"}
                            className="text-sm font-medium text-purple-600 hover:text-purple-700 underline"
                          >
                            Target Addressable Market
                          </a>
                          <Typography
                            as="p"
                            className="text-sm text-tertiary-600"
                          >
                            {engagement.marketStrategy?.targetAddressableMarket?.date ? formatDate(engagement.marketStrategy.targetAddressableMarket.date) : null}
                          </Typography>
                          {engagement.marketStrategy?.targetAddressableMarket?.file?.url && (
                            <Button
                              variant="secondary"
                              size="small"
                              onClick={() => {
                                window.open(engagement.marketStrategy.targetAddressableMarket.file.url, '_blank');
                              }}
                              className="text-purple-600 hover:text-purple-700 border-purple-200 hover:border-purple-300"
                            >
                              View
                            </Button>
                          )}
                        </div>
                      </div>
                    )}
                  </div>
                </div>
              </div>
            )}

            {activeTab === "collaboration" && (
              <div className="flex flex-col gap-6">
                {/* Team Members Card */}
                <div className="grid grid-cols-1 gap-6">
                  {/* Team Members Card */}
                  <Card className="w-full shadow-sm rounded-2xl border border-slate-100">
                    <div className="flex flex-col gap-4">
                      {/* Header */}
                      <div className="flex items-start justify-between">
                        <div className="flex flex-col gap-1">
                          <Typography
                            as="h3"
                            className="text-base font-semibold text-primary-900"
                          >
                            Team Members
                          </Typography>
                          <Typography
                            as="p"
                            className="text-sm text-tertiary-600"
                          >
                            View your existing team members here
                          </Typography>
                        </div>
                        {isPrimaryStudent && !isReadOnly && (
                          <Button
                            variant="secondary"
                            size="small"
                            startIcon={<Icon icon="lucide:user-plus" className="h-4 w-4" />}
                            className="bg-white border border-slate-200 text-slate-700 hover:bg-slate-50"
                            onClick={() => setShowAddTeamMember(true)}
                            disabled={!canAddStudent}
                            title={
                              !canAddStudent
                                ? `This idea has reached its limit of ${maxStudents} students.`
                                : undefined
                            }
                          >
                            Invite Team Member
                          </Button>
                        )}
                        {isPrimaryMentor && !isReadOnly && !isIdeaRejected && canInviteSecondaryMentor && (
                          <Button
                            variant="secondary"
                            size="small"
                            startIcon={<Icon icon="lucide:user-plus" className="h-4 w-4" />}
                            className="bg-white border border-slate-200 text-slate-700 hover:bg-slate-50"
                            onClick={handleOpenInviteMentor}
                          >
                            Invite Mentor
                          </Button>
                        )}
                      </div>

                      {inviteSuccess && (
                        <div className="flex items-center gap-2 rounded-lg border border-green-200 bg-green-50 px-4 py-2.5 text-sm text-green-700">
                          <Icon icon="lucide:check-circle" className="h-4 w-4" />
                          <span>{inviteSuccess}</span>
                        </div>
                      )}

                      {/* Table */}
                      <Table
                        columns={[
                          {
                            header: "Member Name",
                            accessor: "name",
                            render: (value, record) => (
                              <div className="flex items-center gap-3">
                                <Avatar
                                  src={
                                    record.avatar &&
                                      record.avatar.length >= 10 &&
                                      (record.avatar.startsWith("http") ||
                                        record.avatar.startsWith("data:image") ||
                                        record.avatar.startsWith("/"))
                                      ? record.avatar
                                      : null
                                  }
                                  firstName={record.name?.split(" ")[0]}
                                  lastName={record.name?.split(" ").slice(1).join(" ")}
                                  email={record.email}
                                  alt={record.name}
                                  size="w-10 h-10"
                                />
                                <div className="flex flex-col">
                                  <Typography
                                    as="p"
                                    className="text-sm text-primary-900 font-medium"
                                  >
                                    {record.name}
                                  </Typography>
                                  <Typography
                                    as="p"
                                    className="text-xs text-tertiary-500"
                                  >
                                    {record.email}
                                  </Typography>
                                </div>
                              </div>
                            ),
                          },
                          {
                            header: "Member Role",
                            accessor: "role",
                            render: (value) => (
                              <Typography
                                as="p"
                                className="text-sm text-tertiary-600"
                              >
                                {value}
                              </Typography>
                            ),
                          },
                        ]}
                        data={ideaTeamRows
                          .filter(
                            (row) =>
                              row?.role === "PRIMARY_STUDENT" ||
                              row?.role === "SECONDARY_STUDENT" ||
                              row?.role === "SECONDARY_MENTOR"
                          )
                          .map((row) => ({
                            id: row.id,
                            name:
                              `${row.user?.first_name || ""} ${row.user?.last_name || ""}`.trim() ||
                              row.user?.email ||
                              "Unknown",
                            email: row.user?.email || "",
                            avatar: row.user?.profile_picture_url || "",
                            role:
                              row.role === "PRIMARY_STUDENT"
                                ? "Primary (Owner)"
                                : row.role === "SECONDARY_MENTOR"
                                ? "Secondary Mentor"
                                : "Team Member",
                          }))
                          // The primary mentor isn't a row of its own — they're
                          // stored as `mentor_id` on the student rows (resolved to
                          // the `mentor` association). Surface them once, if present.
                          .concat(
                            (() => {
                              const studentRow =
                                ideaTeamRows.find(
                                  (row) =>
                                    row?.role === "PRIMARY_STUDENT" && row?.mentor
                                ) ||
                                ideaTeamRows.find(
                                  (row) =>
                                    row?.role === "SECONDARY_STUDENT" && row?.mentor
                                );
                              const pm = studentRow?.mentor;
                              if (!pm) return [];
                              return [
                                {
                                  id: `primary-mentor-${pm.id}`,
                                  name:
                                    `${pm.first_name || ""} ${pm.last_name || ""}`.trim() ||
                                    pm.email ||
                                    "Unknown",
                                  email: pm.email || "",
                                  avatar: pm.profile_picture_url || "",
                                  role: "Primary Mentor",
                                },
                              ];
                            })()
                          )
                          // Append emailed-but-not-yet-accepted co-founder invites.
                          .concat(
                            pendingTeamInvites.map((inv) => ({
                              id: `invite-${inv.id}`,
                              name:
                                `${inv.first_name || ""} ${inv.last_name || ""}`.trim() ||
                                inv.email ||
                                "Invited member",
                              email: inv.email || "",
                              avatar: "",
                              role: "Team Member (Invited)",
                            }))
                          )}
                        rowKey="id"
                        emptyMessage="No team members added yet."
                        className="w-full"
                      />
                    </div>
                  </Card>

                </div>
              </div>
            )}

            {activeTab === "review" && (
              <ReviewScoresSection
                activeStage={activeStage}
                evaluationStages={evaluationStages}
                evaluationStagesLoading={evaluationStagesLoading}
                evaluationStagesError={evaluationStagesError}
                reviewResponses={reviewResponses}
                setReviewResponses={handleReviewResponsesChange}
                editedRubrics={editedRubrics}
                setEditedRubrics={handleEditedRubricsChange}
                stageReviewOutcomes={stageReviewOutcomes}
                onStageReviewOutcomeChange={handleStageReviewOutcomeChange}
                engagement={engagement}
                reviewLocked={reviewLocked || isIdeaRejected}
                scoresLocked={scoresLocked}
                ideaRejected={isIdeaRejected}
                isSaving={isSaving}
                isAdvancing={isAdvancing}
                hasUnsavedReviewChanges={reviewDirty}
                canAdvanceStage={!reviewLocked && !isIdeaRejected}
                isPrimaryMentor={isPrimaryMentor}
                secondaryReviewers={secondaryReviewers}
                onViewSecondaryScores={(mid) => setViewSecondaryMentorId(mid)}
                nextStageName={
                  IDEA_FLOW_STAGE_NAMES[engagement?.currentStep ?? 1] || "next stage"
                }
                isFinalStage={
                  (engagement?.currentStep ?? 1) >=
                  (evaluationStages?.stages?.length || IDEA_FLOW_STAGE_NAMES.length)
                }
                setAnswerModal={setAnswerModal}
                onSaveScores={handleSaveScores}
                onAdvanceStage={handleAdvanceStage}
              />
            )}
          </div>
        </div>
      )}

      <AddTeamMemberDialog
        isOpen={showAddTeamMember}
        onClose={() => setShowAddTeamMember(false)}
        ideaId={id}
        onAdded={fetchIdeaTeam}
      />

      <IdeaClarityDialog
        isOpen={showClarityDialog}
        onClose={() => setShowClarityDialog(false)}
        onSubmit={handleSaveIdeaClarity}
        recipientEmail={engagement?.submitter?.email || ""}
        recipientName={engagement?.submitter?.name || ""}
        ideaTitle={engagement?.title || ""}
        initialBody={engagement?.ideaClarity || ""}
        defaultCc={user?.email || ""}
        saving={savingClarity}
      />

      {/* Reject Idea confirmation */}
      <Dialog
        isOpen={showRejectDialog}
        onClose={() => {
          if (!rejectingIdea) {
            setShowRejectDialog(false);
            setAdminRejectMode(null);
            setRejectReason("");
            setRejectReasonError("");
          }
        }}
        title={adminRejectMode === "onBehalf" ? "Reject on Mentor's Behalf" : "Reject Idea"}
        description={
          adminRejectMode === "onBehalf"
            ? "This declines the idea for the currently-assigned mentor and forwards it to the student's next preferred mentor. Add a reason that will be recorded with the decision."
            : adminRejectMode === "terminal"
            ? "This terminally rejects the idea and notifies the student. Add a reason that will be recorded with the decision."
            : "This will mark the idea as rejected. Add a reason that will be recorded with the decision."
        }
      >
        <div className="flex flex-col gap-5">
          <div className="flex flex-col gap-2 text-left">
            <label htmlFor="reject-reason" className="text-sm font-medium text-slate-700">
              Reason for rejection <span className="text-red-500">*</span>
            </label>
            <textarea
              id="reject-reason"
              value={rejectReason}
              onChange={(e) => {
                setRejectReason(e.target.value);
                if (rejectReasonError) setRejectReasonError("");
              }}
              rows={5}
              placeholder="e.g. The problem statement needs more validation before mentorship can proceed."
              disabled={rejectingIdea}
              className={`w-full rounded-lg border ${
                rejectReasonError ? "border-red-400" : "border-slate-200"
              } bg-white px-4 py-2.5 text-sm text-slate-900 shadow-sm placeholder:text-slate-400 focus:border-brand-blue focus:outline-none focus:ring-2 focus:ring-brand-blue resize-none disabled:bg-slate-100`}
            />
            {rejectReasonError && (
              <p className="text-xs text-red-500">{rejectReasonError}</p>
            )}
          </div>
          <div className="flex items-center justify-end gap-3">
          <Button
            variant="secondary"
            size="medium"
            onClick={() => {
              setShowRejectDialog(false);
              setAdminRejectMode(null);
              setRejectReason("");
              setRejectReasonError("");
            }}
            disabled={rejectingIdea}
            className="border-slate-200 text-slate-700 hover:bg-slate-50"
          >
            Cancel
          </Button>
          <Button
            variant="primary"
            size="medium"
            onClick={handleConfirmReject}
            disabled={rejectingIdea}
            startIcon={
              rejectingIdea ? (
                <Icon icon="lucide:loader-2" className="h-5 w-5 animate-spin" />
              ) : (
                <Icon icon="lucide:x-circle" className="h-5 w-5" />
              )
            }
            className="bg-red-600 hover:bg-red-700 text-white"
          >
            {rejectingIdea ? "Rejecting..." : "Yes, reject"}
          </Button>
          </div>
        </div>
      </Dialog>

      {/* Reassign Mentor dialog (admin) */}
      <Dialog
        isOpen={showReassignDialog}
        onClose={() => {
          if (!reassigning) setShowReassignDialog(false);
        }}
        title="Reassign Mentor"
        description="Select a mentor to assign to this idea. The currently assigned mentor will be removed."
        maxWidth="max-w-2xl"
      >
        <div className="flex flex-col gap-4">
          {engagement?.mentor?.name && (
            <div className="flex items-center gap-2 rounded-lg border border-slate-200 bg-slate-50 px-4 py-3 text-sm text-slate-600">
              <Icon icon="lucide:info" className="h-4 w-4 text-slate-400" />
              <span>
                Current mentor: <span className="font-medium text-slate-800">{engagement.mentor.name}</span>
              </span>
            </div>
          )}

          {reassignError && (
            <Typography as="p" className="text-sm text-red-500">
              {reassignError}
            </Typography>
          )}

          {loadingMentors ? (
            <div className="flex items-center justify-center py-10 text-slate-500 gap-2">
              <Icon icon="lucide:loader-2" className="h-5 w-5 animate-spin" />
              Loading mentors...
            </div>
          ) : mentors.length === 0 ? (
            <div className="flex flex-col items-center justify-center gap-2 py-10 text-center text-slate-500">
              <Icon icon="lucide:users" className="h-7 w-7 text-slate-400" />
              <Typography as="p" className="text-sm">No mentors available.</Typography>
            </div>
          ) : (
            <div className="flex flex-col gap-2 max-h-[50vh] overflow-y-auto pr-1">
              {mentors.map((mentor) => {
                const mentorName = `${mentor.first_name || ""} ${mentor.last_name || ""}`.trim() || mentor.email || "Mentor";
                const isCurrent = String(mentor.id) === String(engagement?.mentor?.id);
                return (
                  <button
                    key={mentor.id}
                    type="button"
                    disabled={reassigning || isCurrent}
                    onClick={() => handleReassignMentor(mentor)}
                    className="flex items-center justify-between gap-3 rounded-xl border border-slate-200 px-4 py-3 text-left transition-colors hover:border-violet-300 hover:bg-violet-50 disabled:cursor-not-allowed disabled:opacity-60"
                  >
                    <div className="flex items-center gap-3 min-w-0">
                      <Avatar
                        src={
                          mentor.profile_picture_url &&
                          mentor.profile_picture_url.length >= 10 &&
                          (mentor.profile_picture_url.startsWith("http") ||
                            mentor.profile_picture_url.startsWith("data:image") ||
                            mentor.profile_picture_url.startsWith("/"))
                            ? mentor.profile_picture_url
                            : null
                        }
                        firstName={mentor.first_name}
                        lastName={mentor.last_name}
                        email={mentor.email}
                        alt={mentorName}
                        size="w-10 h-10"
                      />
                      <div className="flex flex-col min-w-0">
                        <Typography as="p" className="text-sm font-medium text-primary-900 truncate">
                          {mentorName}
                        </Typography>
                        {mentor.email && (
                          <Typography as="p" className="text-xs text-tertiary-500 truncate">
                            {mentor.email}
                          </Typography>
                        )}
                      </div>
                    </div>
                    {isCurrent ? (
                      <span className="text-xs font-medium text-slate-400 shrink-0">Current</span>
                    ) : (
                      <span className="inline-flex items-center gap-1 text-sm font-medium text-violet-600 shrink-0">
                        <Icon icon="lucide:user-check" className="h-4 w-4" />
                        Assign
                      </span>
                    )}
                  </button>
                );
              })}
            </div>
          )}

          {reassigning && (
            <div className="flex items-center justify-center gap-2 text-sm text-violet-600">
              <Icon icon="lucide:loader-2" className="h-4 w-4 animate-spin" />
              Reassigning mentor...
            </div>
          )}
        </div>
      </Dialog>

      {/* Invite secondary mentor dialog (primary mentor) */}
      <Dialog
        isOpen={showInviteMentor}
        onClose={() => {
          if (!invitingMentor) setShowInviteMentor(false);
        }}
        title="Invite a Mentor"
        description={`Invite another mentor to co-mentor this idea. They'll see the invitation under Review Ideas and become a secondary mentor once they accept. An idea can have one primary mentor and up to ${maxSecondaryMentors} secondary mentor${maxSecondaryMentors === 1 ? "" : "s"}.`}
        maxWidth="max-w-2xl"
      >
        <div className="flex flex-col gap-4">
          {inviteError && (
            <Typography as="p" className="text-sm text-red-500">
              {inviteError}
            </Typography>
          )}

          {loadingInviteMentors ? (
            <div className="flex items-center justify-center py-10 text-slate-500 gap-2">
              <Icon icon="lucide:loader-2" className="h-5 w-5 animate-spin" />
              Loading mentors...
            </div>
          ) : inviteMentors.length === 0 ? (
            <div className="flex flex-col items-center justify-center gap-2 py-10 text-center text-slate-500">
              <Icon icon="lucide:users" className="h-7 w-7 text-slate-400" />
              <Typography as="p" className="text-sm">No other mentors available to invite.</Typography>
            </div>
          ) : (
            <>
              {/* Search + industry filter */}
              <div className="flex flex-col gap-3 sm:flex-row">
                <div className="relative flex-1">
                  <Icon
                    icon="lucide:search"
                    className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-400"
                  />
                  <input
                    type="text"
                    value={inviteSearch}
                    onChange={(e) => setInviteSearch(e.target.value)}
                    placeholder="Search by name, email, or expertise"
                    className="w-full rounded-lg border border-slate-200 bg-white py-2.5 pl-9 pr-3 text-sm text-slate-900 placeholder:text-slate-400 focus:border-[#7F56D9] focus:outline-none focus:ring-2 focus:ring-purple-100"
                  />
                </div>
                <div className="relative sm:w-56">
                  <select
                    value={inviteIndustryFilter}
                    onChange={(e) => setInviteIndustryFilter(e.target.value)}
                    className="w-full appearance-none rounded-lg border border-slate-200 bg-white py-2.5 pl-3 pr-9 text-sm text-slate-900 focus:border-[#7F56D9] focus:outline-none focus:ring-2 focus:ring-purple-100"
                  >
                    <option value="">All industries</option>
                    {inviteIndustryOptions.map((industry) => (
                      <option key={industry} value={industry}>
                        {industry}
                      </option>
                    ))}
                  </select>
                  <Icon
                    icon="lucide:chevron-down"
                    className="pointer-events-none absolute right-3 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-400"
                  />
                </div>
              </div>

              {filteredInviteMentors.length === 0 ? (
                <div className="flex flex-col items-center justify-center gap-2 py-10 text-center text-slate-500">
                  <Icon icon="lucide:search-x" className="h-7 w-7 text-slate-400" />
                  <Typography as="p" className="text-sm">
                    No mentors match your search.
                  </Typography>
                  <button
                    type="button"
                    onClick={() => {
                      setInviteSearch("");
                      setInviteIndustryFilter("");
                    }}
                    className="text-sm font-medium text-[#7F56D9] hover:underline"
                  >
                    Clear filters
                  </button>
                </div>
              ) : (
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 max-h-[60vh] overflow-y-auto pr-1 py-1">
                  {filteredInviteMentors.map((mentor) => {
                const mentorName = `${mentor.first_name || ""} ${mentor.last_name || ""}`.trim() || mentor.email || "Mentor";
                const avatarSrc =
                  mentor.profile_picture_url &&
                  mentor.profile_picture_url.length >= 10 &&
                  (mentor.profile_picture_url.startsWith("http") ||
                    mentor.profile_picture_url.startsWith("data:image") ||
                    mentor.profile_picture_url.startsWith("/"))
                    ? mentor.profile_picture_url
                    : null;
                return (
                  <Card
                    key={mentor.id}
                    className="relative flex min-h-[20rem] flex-col items-center gap-2 p-6 border-2 border-slate-200 text-center transition-all hover:border-[#7F56D9] hover:bg-purple-50"
                  >
                    {/* Profile Picture */}
                    <div className="flex w-full justify-center">
                      <Avatar
                        src={avatarSrc}
                        firstName={mentor.first_name}
                        lastName={mentor.last_name}
                        email={mentor.email}
                        alt={mentorName}
                        size="w-20 h-20"
                        className="mx-auto"
                      />
                    </div>

                    {/* Name */}
                    <Typography as="h3" className="mt-2 text-lg font-bold text-primary-900 line-clamp-1">
                      {mentorName}
                    </Typography>

                    {/* Title */}
                    <Typography as="p" className="text-sm font-medium text-[#7F56D9] line-clamp-2 min-h-[2.5rem]">
                      {getInviteMentorTitle(mentor)}
                    </Typography>

                    {/* Email */}
                    {mentor.email && (
                      <Typography as="p" className="text-xs text-tertiary-500 truncate max-w-full">
                        {mentor.email}
                      </Typography>
                    )}

                    {/* Description */}
                    <Typography as="p" className="text-xs text-tertiary-600 line-clamp-2">
                      {getInviteMentorDescription(mentor)}
                    </Typography>

                    {/* Social Icons */}
                    {(mentor.linkedin_url || mentor.x_url || mentor.github_url) && (
                      <div className="flex justify-center gap-3 pt-1">
                        {mentor.linkedin_url && (
                          <a
                            href={mentor.linkedin_url}
                            target="_blank"
                            rel="noopener noreferrer"
                            onClick={(e) => e.stopPropagation()}
                            className="text-slate-400 hover:text-[#7F56D9] transition-colors"
                          >
                            <Icon icon="mdi:linkedin" className="w-5 h-5" />
                          </a>
                        )}
                        {mentor.x_url && (
                          <a
                            href={mentor.x_url}
                            target="_blank"
                            rel="noopener noreferrer"
                            onClick={(e) => e.stopPropagation()}
                            className="text-slate-400 hover:text-[#7F56D9] transition-colors"
                          >
                            <Icon icon="mdi:twitter" className="w-5 h-5" />
                          </a>
                        )}
                        {mentor.github_url && (
                          <a
                            href={mentor.github_url}
                            target="_blank"
                            rel="noopener noreferrer"
                            onClick={(e) => e.stopPropagation()}
                            className="text-slate-400 hover:text-[#7F56D9] transition-colors"
                          >
                            <Icon icon="mdi:github" className="w-5 h-5" />
                          </a>
                        )}
                      </div>
                    )}

                    {/* Invite Button */}
                    <Button
                      variant="primary"
                      size="small"
                      disabled={invitingMentor}
                      startIcon={<Icon icon="lucide:send" className="h-4 w-4" />}
                      className="mt-auto w-full bg-[#7F56D9] hover:bg-purple-700 text-white"
                      onClick={() => handleInviteMentor(mentor)}
                    >
                      Invite
                    </Button>
                  </Card>
                );
                  })}
                </div>
              )}
            </>
          )}

          {invitingMentor && (
            <div className="flex items-center justify-center gap-2 text-sm text-violet-600">
              <Icon icon="lucide:loader-2" className="h-4 w-4 animate-spin" />
              Sending invitation...
            </div>
          )}
        </div>
      </Dialog>

      {/* Secondary mentor score view — visible only to the primary mentor */}
      <Dialog
        isOpen={!!viewSecondaryMentorId}
        onClose={() => setViewSecondaryMentorId(null)}
        title={
          selectedSecondaryReview
            ? `Review score by ${selectedSecondaryReview.name}`
            : "Secondary mentor review"
        }
        description={`${
          activeStageData?.stageNumber
            ? `Stage ${activeStageData.stageNumber}`
            : "This stage"
        } · Scores and notes submitted by this secondary mentor — visible only to you.`}
        maxWidth="max-w-2xl"
      >
        {selectedSecondaryReview && (
          <div className="flex flex-col gap-4">
            <div className="flex items-start gap-2 rounded-xl border border-amber-200 bg-amber-50 px-4 py-3">
              <Icon icon="lucide:info" className="mt-0.5 h-4 w-4 shrink-0 text-amber-600" />
              <Typography as="p" className="text-sm text-amber-800">
                Please review the secondary mentor's scores and feedback carefully
                before finalizing the student's scores for this stage.
              </Typography>
            </div>
            <div className="flex items-center justify-between rounded-xl border border-violet-100 bg-violet-50 px-4 py-3">
              <Typography as="p" className="text-sm font-medium text-primary-900">
                Overall score
              </Typography>
              <Typography as="p" className="text-lg font-bold text-violet-700">
                {selectedSecondaryReview.total}
                <span className="text-sm font-medium text-violet-400">
                  /{selectedSecondaryReview.maxTotal}
                </span>
              </Typography>
            </div>
            <div className="rounded-xl border border-slate-200 p-4">
              <div className="flex items-center gap-2">
                <Icon
                  icon="lucide:message-square-warning"
                  className="h-4 w-4 text-amber-600"
                />
                <Typography
                  as="h4"
                  className="text-xs font-bold uppercase tracking-wide text-slate-600"
                >
                  Review Outcome & Feedback
                </Typography>
              </div>
              {selectedSecondaryReview.outcome?.trim() ? (
                <Typography
                  as="p"
                  className="mt-2 whitespace-pre-wrap text-sm text-tertiary-600"
                >
                  {selectedSecondaryReview.outcome}
                </Typography>
              ) : (
                <Typography as="p" className="mt-2 text-xs italic text-slate-400">
                  No review outcome provided.
                </Typography>
              )}
            </div>
            <div className="flex max-h-[55vh] flex-col gap-3 overflow-y-auto pr-1">
              {selectedSecondaryReview.questions.map((q, idx) => (
                <div key={idx} className="rounded-xl border border-slate-200 p-4">
                  <div className="flex items-start justify-between gap-3">
                    <Typography as="p" className="text-sm font-medium text-primary-900">
                      {q.question}
                    </Typography>
                    <span className="shrink-0 rounded-lg bg-slate-100 px-2.5 py-1 text-sm font-semibold text-slate-700">
                      {q.score != null ? `${q.score}/10` : "—/10"}
                    </span>
                  </div>
                  {q.answer ? (
                    <Typography
                      as="p"
                      className="mt-2 whitespace-pre-wrap text-sm text-tertiary-600"
                    >
                      {q.answer}
                    </Typography>
                  ) : (
                    <Typography as="p" className="mt-2 text-xs italic text-slate-400">
                      No note added.
                    </Typography>
                  )}
                </div>
              ))}
            </div>
          </div>
        )}
      </Dialog>

      {answerModal && (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center bg-slate-900/50 backdrop-blur-sm p-4"
          onClick={() => setAnswerModal(null)}
        >
          <div
            className="bg-white rounded-2xl shadow-2xl w-full max-w-lg overflow-hidden"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="px-6 py-4 border-b border-slate-200 flex items-start justify-between gap-4">
              <div>
                <Typography as="p" className="text-xs font-semibold uppercase tracking-wide text-purple-600">
                  Answer
                </Typography>
                <Typography as="h3" className="text-base font-semibold text-primary-900 mt-1">
                  {answerModal.question}
                </Typography>
              </div>
              <button
                className="p-1 rounded-lg hover:bg-slate-100 transition-colors flex-shrink-0"
                onClick={() => setAnswerModal(null)}
              >
                <Icon icon="lucide:x" className="h-5 w-5 text-slate-500" />
              </button>
            </div>
            <div className="px-6 py-5">
              <textarea
                autoFocus
                rows={6}
                placeholder={answerModal.readOnly ? "No answer provided." : "Type your answer for this question..."}
                value={answerModal.draft}
                readOnly={answerModal.readOnly}
                onChange={(e) => setAnswerModal((m) => ({ ...m, draft: e.target.value }))}
                className={`w-full px-3 py-2 text-sm border border-slate-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-purple-500 focus:border-transparent resize-none ${answerModal.readOnly ? "bg-slate-50 cursor-not-allowed" : ""}`}
              />
            </div>
            <div className="px-6 py-4 border-t border-slate-200 flex justify-end gap-3 bg-slate-50">
              <Button variant="outline" onClick={() => setAnswerModal(null)}>
                {answerModal.readOnly ? "Close" : "Cancel"}
              </Button>
              {!answerModal.readOnly && (
              <Button
                onClick={() => {
                  setReviewDirty(true);
                  setReviewResponses((prev) => ({
                    ...prev,
                    [answerModal.stageId]: {
                      ...(prev[answerModal.stageId] || {}),
                      [answerModal.questionId]: {
                        ...(prev[answerModal.stageId]?.[answerModal.questionId] || {}),
                        answer: answerModal.draft,
                      },
                    },
                  }));
                  setAnswerModal(null);
                }}
              >
                Save Answer
              </Button>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default MentorEngagementDetail;
